// inventory.js — Inventory admin console (Stock list + Requests queue).
//
// Two views share this module: the Stock list (init) and the Requests queue
// (initRequests). DOM is resolved lazily on first show (sections are hidden at
// boot). All server calls go through api.fetch (cookie auth; throws on !ok).

import { escapeHtml } from '../../src/lib/format.js';
import { openDialog, closeDialog } from '../../src/lib/dialog.js';
import { badge, titleCase, activePill } from '../../src/lib/hr-ui.js';
import { hasRole } from '../../src/lib/roles.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

const API = api.routes;
const flash = (m, t) => notify.showFlash(m, t);

// ---------------------------------------------------------------------------
// Shared caches
// ---------------------------------------------------------------------------

let categories = [];
let locations = [];

async function ensureLookups() {
  if (!categories.length) {
    const c = await api.fetch(API.inventoryCategories('active_only=true'), { method: 'GET' });
    categories = c.items || [];
  }
  if (!locations.length) {
    const l = await api.fetch(API.inventoryLocations('active_only=true'), { method: 'GET' });
    locations = l.items || [];
  }
}

function categoryName(id) {
  const c = categories.find(x => x.id === id);
  return c ? c.name : null;
}

// ---------------------------------------------------------------------------
// Canonical mappings — the single source of truth for how inventory vocab
// renders. All of these use the admin `.badge` system (admin.css); no bespoke
// solid pills. See the remediation doc §1.
// ---------------------------------------------------------------------------

// Classification → { label, variant, hint }. Variants map to .badge.* classes.
const CLASS_META = {
  customer: { label: 'Customer equipment', variant: 'info', hint: 'Customer equipment can only be issued to tickets — never deployed to infrastructure.' },
  infrastructure: { label: 'Infrastructure asset', variant: 'violet', hint: 'Infrastructure is deployed to sites/facilities — not issued to customers.' },
  consumable: { label: 'Consumable', variant: 'off', hint: 'Consumables can be issued or deployed, and are always consumed on use.' },
  tool: { label: 'Tool / plant', variant: 'teal', hint: 'Tools are assigned to a person and returned — not consumed or issued.' },
};

function classBadge(cls) {
  const m = CLASS_META[cls];
  return badge(m ? m.label : (cls || '—'), m ? m.variant : '');
}

// Movement type → badge variant.
const MOVE_BADGE = {
  receive: 'ok', return: 'ok', issue: 'warn', adjust: 'warn', write_off: 'warn', dispose: 'warn',
  deploy: 'violet', deinstall: 'info', transfer: 'info', assign: 'teal', reserve: 'off', consume: 'off',
};

function movementBadge(type) {
  return badge(titleCase(type || ''), MOVE_BADGE[type] || '');
}

// Stock status (derive from available vs reorder).
function stockStatus({ available, below_reorder }) {
  const avail = Number(available);
  if (!(avail > 0)) return { label: 'Out of stock', variant: 'warn' };
  if (below_reorder) return { label: 'Low', variant: 'off' };
  return { label: 'In stock', variant: 'ok' };
}

function stockStatusBadge(row) {
  const s = stockStatus(row);
  return badge(s.label, s.variant);
}

// Request status → badge variant.
const REQ_BADGE = {
  submitted: 'info', approved: 'off', partially_approved: 'off', reserved: 'info',
  fulfilled: 'ok', delivered: 'ok', rejected: 'warn', cancelled: 'warn',
};
// PO status → badge variant.
const PO_BADGE = {
  draft: '', ordered: 'off', in_transit: 'info', partially_received: 'off', received: 'ok', cancelled: 'warn',
};

function fillSelect(sel, items, { valueKey = 'id', labelFn, placeholder }) {
  if (!sel) return;
  const opts = [placeholder != null ? `<option value="">${escapeHtml(placeholder)}</option>` : '']
    .concat(items.map(i => `<option value="${escapeHtml(i[valueKey])}">${escapeHtml(labelFn(i))}</option>`));
  sel.innerHTML = opts.join('');
}

// ===========================================================================
// STOCK VIEW
// ===========================================================================

let stockDom = false;
let el = {};
let activeItem = null;

function resolveStockDom() {
  if (stockDom) return;
  stockDom = true;
  const g = (id) => document.getElementById(id);
  el = {
    kpiSkus: g('invKpiSkus'), kpiLow: g('invKpiLow'), kpiOut: g('invKpiOut'), kpiReserved: g('invKpiReserved'),
    search: g('invSearch'), categoryFilter: g('invCategoryFilter'), warehouseFilter: g('invWarehouseFilter'),
    statusFilter: g('invStatusFilter'), refreshBtn: g('invRefreshBtn'), scanBtn: g('invScanBtn'),
    receiveTopBtn: g('invReceiveTopBtn'), newItemBtn: g('invNewItemBtn'),
    tbody: g('invTbody'),
    pagerPrev: g('invPagerPrev'), pagerNext: g('invPagerNext'), pagerInfo: g('invPagerInfo'),
    sortName: g('invSortName'), sortCategory: g('invSortCategory'),
    sortCaretName: g('invSortCaretName'), sortCaretCategory: g('invSortCaretCategory'),
    // dialogs
    itemDlg: g('invItemDlg'), itemForm: g('invItemForm'), itemSku: g('invItemSku'), itemName: g('invItemName'),
    itemCategory: g('invItemCategory'), itemClassification: g('invItemClassification'), itemTracking: g('invItemTracking'),
    itemTrackingField: g('invItemTrackingField'), itemSubmit: g('invItemSubmit'),
    itemOpeningSection: g('invItemOpeningSection'), itemEditNote: g('invItemEditNote'),
    itemDlgTitle: g('invItemDlgTitle'), itemClassSeg: g('invItemClassSeg'), itemClassHint: g('invItemClassHint'),
    itemFixedAssetNote: g('invItemFixedAssetNote'), itemCategoryNewBtn: g('invItemCategoryNewBtn'),
    itemUnit: g('invItemUnit'), itemReorder: g('invItemReorder'), itemReorderField: g('invItemReorderField'),
    itemAccessTech: g('invItemAccessTech'), itemAccessTechField: g('invItemAccessTechField'),
    itemOpeningQty: g('invItemOpeningQty'), itemOpeningQtyField: g('invItemOpeningQtyField'),
    itemOpeningSerials: g('invItemOpeningSerials'), itemOpeningSerialsField: g('invItemOpeningSerialsField'),
    itemOpeningLocation: g('invItemOpeningLocation'), itemOpeningFile: g('invItemOpeningFile'),
    receiveDlg: g('invReceiveDlg'), receiveForm: g('invReceiveForm'), receiveItemName: g('invReceiveItemName'),
    receiveItemField: g('invReceiveItemField'), receiveItemPickField: g('invReceiveItemPickField'), receiveItem: g('invReceiveItem'),
    receiveLocation: g('invReceiveLocation'), receiveQtyField: g('invReceiveQtyField'), receiveQty: g('invReceiveQty'),
    receiveSerialsField: g('invReceiveSerialsField'), receiveSerials: g('invReceiveSerials'),
    receiveUnitCost: g('invReceiveUnitCost'), receiveDocType: g('invReceiveDocType'), receiveDocRef: g('invReceiveDocRef'),
    receiveFile: g('invReceiveFile'), receiveTitle: g('invReceiveTitle'),
    issueDlg: g('invIssueDlg'), issueForm: g('invIssueForm'), issueItemName: g('invIssueItemName'),
    issueLocation: g('invIssueLocation'), issueQty: g('invIssueQty'), issueSerial: g('invIssueSerial'),
    issueSeg: g('invIssueSeg'), issueDestKind: g('invIssueDestKind'),
    issueTargetField: g('invIssueTargetField'), issueTarget: g('invIssueTarget'),
    issueTechField: g('invIssueTechField'), issueTech: g('invIssueTech'),
    issueWorkOrderNote: g('invIssueWorkOrderNote'), issueRecordedUnder: g('invIssueRecordedUnder'),
    // adaptive move dialog
    moveDlg: g('invMoveDlg'), moveForm: g('invMoveForm'), moveTitle: g('invMoveTitle'), moveItemName: g('invMoveItemName'),
    moveFrom: g('invMoveFrom'), moveTo: g('invMoveTo'), moveQty: g('invMoveQty'), moveSerial: g('invMoveSerial'),
    moveCurrent: g('invMoveCurrent'), moveCurrentField: g('invMoveCurrentField'), moveQtyLabel: g('invMoveQtyLabel'), moveDelta: g('invMoveDelta'),
    moveFacility: g('invMoveFacility'), moveCondition: g('invMoveCondition'), moveAssignee: g('invMoveAssignee'),
    moveDisposal: g('invMoveDisposal'), moveReason: g('invMoveReason'), moveHint: g('invMoveHint'),
    moveFromField: g('invMoveFromField'), moveToField: g('invMoveToField'), moveQtyField: g('invMoveQtyField'),
    moveSerialField: g('invMoveSerialField'), moveFacilityField: g('invMoveFacilityField'),
    moveConditionField: g('invMoveConditionField'), moveAssigneeField: g('invMoveAssigneeField'),
    moveDisposalField: g('invMoveDisposalField'), moveReasonField: g('invMoveReasonField'),
  };
}

// Movement-type → dialog config: which fields show, the endpoint, and (for the
// approval-gated moves) the approval subject.
const MOVE_CFG = {
  transfer: { title: 'Transfer stock', fields: ['from', 'to', 'qty', 'serial'], endpoint: 'inventoryMovementTransfer' },
  // No manual 'deploy' here: stock reaches a facility only through a work order
  // (Operations), never from the stock screens.
  deinstall: { title: 'De-install', fields: ['to', 'qty', 'serial', 'facility', 'condition'], endpoint: 'inventoryMovementDeinstall' },
  assign: { title: 'Assign to person', fields: ['from', 'qty', 'serial', 'assignee'], endpoint: 'inventoryMovementAssign' },
  return: { title: 'Return from person', fields: ['to', 'qty', 'serial', 'condition'], endpoint: 'inventoryMovementReturn' },
  adjust: { title: 'Adjust — stock-count correction', fields: ['from', 'current', 'qty', 'reason'], endpoint: 'inventoryMovementAdjust', gated: 'adjustment' },
  write_off: { title: 'Write off — lost / damaged', fields: ['from', 'qty', 'serial', 'reason'], endpoint: 'inventoryMovementWriteOff', gated: 'write_off' },
  dispose: { title: 'Dispose', fields: ['from', 'qty', 'serial', 'reason', 'disposal'], endpoint: 'inventoryMovementDispose', gated: 'disposal' },
};

let activeMoveType = null;
let _adjustStock = [];  // stock_by_location for the item being adjusted
let _moveUnitId = null; // set when acting on one specific asset unit

// opts.serial / opts.fromLocationId pre-fill the dialog when acting on one known
// unit (from the asset detail) — serial is locked so you can't retarget it.
function openMove(item, type, opts = {}) {
  const cfg = MOVE_CFG[type];
  if (!cfg || !el.moveDlg) return;
  activeMoveType = type;
  activeItem = item;
  _moveUnitId = opts.unitId || null;
  ensureLookups().then(() => {
    el.moveForm.reset();
    el.moveTitle.textContent = cfg.title;
    el.moveItemName.value = `${item.sku} — ${item.name}`;
    fillSelect(el.moveFrom, locations, { labelFn: l => l.name, placeholder: 'Select…' });
    fillSelect(el.moveTo, locations.filter(l => l.kind !== 'van'), { labelFn: l => l.name, placeholder: 'Select…' });
    const show = new Set(cfg.fields);
    // A serial only identifies a unit of a *serialized* item; a bulk movement is
    // by quantity, so don't ask for one.
    if (item.tracking !== 'serialized') show.delete('serial');
    const map = {
      from: el.moveFromField, to: el.moveToField, qty: el.moveQtyField, serial: el.moveSerialField,
      facility: el.moveFacilityField, condition: el.moveConditionField, assignee: el.moveAssigneeField,
      disposal: el.moveDisposalField, reason: el.moveReasonField, current: el.moveCurrentField,
    };
    Object.entries(map).forEach(([k, node]) => { if (node) node.style.display = show.has(k) ? '' : 'none'; });
    el.moveQty.value = '1';
    el.moveHint.textContent = cfg.gated ? 'Raises a pending approval — a different finance user must authorise it before it posts.' : '';

    // Adjust is a count correction, not a blind delta: relabel Quantity as the
    // COUNTED figure, show current on-hand, and preview the resulting write.
    const isAdjust = type === 'adjust';
    if (el.moveDelta) el.moveDelta.style.display = isAdjust ? '' : 'none';
    if (el.moveQtyLabel) el.moveQtyLabel.textContent = isAdjust ? 'Counted quantity *' : 'Quantity *';
    _adjustStock = [];
    if (isAdjust) {
      el.moveQty.value = '';
      el.moveDelta.textContent = 'Enter the counted quantity to see the correction.';
      // Fetch per-location on-hand so we can show current + compute the delta.
      api.fetch(API.inventoryItem(item.id), { method: 'GET' })
        .then(d => { _adjustStock = d.stock_by_location || []; updateAdjustDelta(); })
        .catch(() => {});
    }

    // Acting on one specific unit (from the asset detail): lock its serial + default
    // the from-location to where the unit currently sits.
    if (el.moveSerial) {
      el.moveSerial.value = opts.serial || '';
      el.moveSerial.readOnly = !!opts.serial;
    }
    if (opts.fromLocationId && el.moveFrom) el.moveFrom.value = opts.fromLocationId;

    openDialog(el.moveDlg);
  }).catch(() => flash('Failed to load locations', 'error'));
}

// Current on-hand for the adjust dialog's selected location.
function adjustCurrentOnHand() {
  const row = _adjustStock.find(s => s.location_id === el.moveFrom.value);
  return row ? Number(row.on_hand || 0) : 0;
}

// Refresh the "current on-hand" readout and the +/- preview as the location or
// counted quantity change.
function updateAdjustDelta() {
  if (activeMoveType !== 'adjust') return;
  const current = adjustCurrentOnHand();
  el.moveCurrent.value = String(current);
  const raw = el.moveQty.value.trim();
  if (raw === '') { el.moveDelta.textContent = 'Enter the counted quantity to see the correction.'; return; }
  const counted = Number(raw);
  if (Number.isNaN(counted)) { el.moveDelta.textContent = ''; return; }
  const delta = counted - current;
  el.moveDelta.textContent = delta === 0
    ? 'No change — counted matches on-hand.'
    : (delta > 0 ? `Will write UP ${fmt(delta)} (→ ${fmt(counted)}).` : `Will write DOWN ${fmt(-delta)} (→ ${fmt(counted)}).`);
}

async function onMove(ev) {
  ev.preventDefault();
  if (!activeItem || !activeMoveType) return;
  const cfg = MOVE_CFG[activeMoveType];
  const serial = el.moveSerial.value.trim();
  const qty = el.moveQty.value;
  // Adjust posts the signed delta between the counted figure and current on-hand,
  // not the raw number typed — so the operator sets a level, we compute the write.
  let postQty = qty;
  if (activeMoveType === 'adjust') {
    if (!el.moveFrom.value) { flash('Pick the location to correct.', 'error'); return; }
    if (el.moveQty.value.trim() === '' || Number.isNaN(Number(el.moveQty.value))) { flash('Enter the counted quantity.', 'error'); return; }
    const delta = Number(el.moveQty.value) - adjustCurrentOnHand();
    if (delta === 0) { flash('No change — counted matches on-hand.', 'error'); return; }
    postQty = String(delta);
  }
  try {
    // Gated moves (adjust / write-off / dispose) never post directly — they raise
    // a PENDING approval for a *different* finance approver to authorise, and the
    // approval is what posts the movement (two-person control). See the Approvals tab.
    if (cfg.gated) {
      if (!el.moveReason.value.trim()) { flash('Enter a reason for the approval.', 'error'); return; }
      const body = {
        subject: cfg.gated, item_id: activeItem.id, quantity: postQty,
        reason: el.moveReason.value.trim(), location_id: el.moveFrom.value || null,
      };
      if (activeMoveType === 'dispose') body.disposal_method = el.moveDisposal.value;
      if (serial) body.stock_unit_id = await resolveUnitId(serial);
      await api.fetch(API.inventoryApprovals(), { method: 'POST', body: JSON.stringify(body) });
      closeDialog(el.moveDlg);
      flash('Sent to the approvals queue — a finance approver must authorise it.', 'success');
      if (_moveUnitId && assetsDom) { loadAssets(); openAssetDetail(_moveUnitId); }
      return;
    }
    const body = { item_id: activeItem.id };
    if (serial) body.stock_unit_id = await resolveUnitId(serial);
    switch (activeMoveType) {
      case 'transfer': Object.assign(body, { from_location_id: el.moveFrom.value, to_location_id: el.moveTo.value, quantity: qty }); break;
      case 'deinstall': Object.assign(body, { to_location_id: el.moveTo.value, facility_ref: el.moveFacility.value.trim() || null, condition: el.moveCondition.value, quantity: qty }); break;
      case 'assign': Object.assign(body, { from_location_id: el.moveFrom.value, assignee_user_id: el.moveAssignee.value ? Number(el.moveAssignee.value) : null, quantity: qty }); break;
      case 'return': Object.assign(body, { to_location_id: el.moveTo.value, condition: el.moveCondition.value, quantity: qty }); break;
    }
    await api.fetch(API[cfg.endpoint](), { method: 'POST', body: JSON.stringify(body) });
    closeDialog(el.moveDlg);
    flash('Movement posted', 'success');
    if (_moveUnitId) {
      if (assetsDom) { loadAssets(); openAssetDetail(_moveUnitId); }
    } else {
      await loadStock();
      openItemDetail(activeItem.id);
    }
  } catch (err) {
    flash(err.message || 'Movement failed', 'error');
  }
}

let stockWired = false;

function wireStock() {
  if (stockWired) return;
  stockWired = true;
  const reload = () => loadStock();
  el.search && el.search.addEventListener('input', debounce(reload, 250));
  el.categoryFilter && el.categoryFilter.addEventListener('change', reload);
  el.warehouseFilter && el.warehouseFilter.addEventListener('change', reload);
  el.statusFilter && el.statusFilter.addEventListener('change', reload);
  el.refreshBtn && el.refreshBtn.addEventListener('click', reload);

  // KPI cards double as quick filters: click "Out of stock" / "Low stock" to
  // filter the list to those items, and click again to clear back to Active.
  const wireKpiFilter = (valueNode, statusValue, hint) => {
    const card = valueNode && valueNode.closest('.inv-card');
    if (!card || !el.statusFilter) return;
    card.style.cursor = 'pointer';
    card.setAttribute('role', 'button');
    card.setAttribute('tabindex', '0');
    card.title = hint;
    const toggle = () => {
      el.statusFilter.value = el.statusFilter.value === statusValue ? 'active' : statusValue;
      loadStock();
    };
    card.addEventListener('click', toggle);
    card.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); toggle(); }
    });
  };
  wireKpiFilter(el.kpiOut, 'out', 'Show only out-of-stock items');
  wireKpiFilter(el.kpiLow, 'low', 'Show only low-stock items');
  el.scanBtn && el.scanBtn.addEventListener('click', onScan);
  el.receiveTopBtn && el.receiveTopBtn.addEventListener('click', () => openReceive(null));
  el.newItemBtn && el.newItemBtn.addEventListener('click', openNewItem);

  el.tbody && el.tbody.addEventListener('click', (ev) => {
    // Kebab open/close.
    const menuBtn = ev.target.closest('button[data-inv-menu]');
    if (menuBtn) {
      ev.stopPropagation();
      const id = menuBtn.dataset.invMenu;
      const menu = el.tbody.querySelector(`.hr-menu[data-inv-menu-for="${id}"]`);
      const isOpen = menu && !menu.hidden;
      closeKebabs();
      if (menu && !isOpen) { menu.hidden = false; menuBtn.setAttribute('aria-expanded', 'true'); }
      return;
    }
    // Kebab action.
    const actBtn = ev.target.closest('button[data-inv-action]');
    if (actBtn) {
      ev.stopPropagation();
      const item = _stockRows[actBtn.dataset.invTarget];
      closeKebabs();
      if (item) runItemAction(item, actBtn.dataset.invAction);
      return;
    }
    // Inline-detail close button.
    if (ev.target.closest('button[data-stock-close]')) {
      ev.stopPropagation();
      closeItemDetail();
      return;
    }
    // Inline-detail action bar (Receive / Transfer / … act on the open item).
    const detailBtn = ev.target.closest('.inv-inline-actions button[data-action]');
    if (detailBtn) {
      ev.stopPropagation();
      if (activeItem) runItemAction(activeItem, detailBtn.dataset.action);
      return;
    }
    // Row click → toggle the inline detail.
    const row = ev.target.closest('tr[data-item-row]');
    if (row) toggleItemDetail(row.dataset.itemRow);
  });

  // Movement-window date change (inputs live inside the open inline detail).
  el.tbody && el.tbody.addEventListener('change', (ev) => {
    const fromInp = ev.target.closest('[data-ledger-from]');
    const toInp = ev.target.closest('[data-ledger-to]');
    if (!fromInp && !toInp) return;
    if (fromInp) _ledgerFrom = fromInp.value;
    if (toInp) _ledgerTo = toInp.value;
    if (_stockOpenId) fetchLedger(_stockOpenId);
  });

  // Pager.
  el.pagerPrev && el.pagerPrev.addEventListener('click', () => setStockPage(-1));
  el.pagerNext && el.pagerNext.addEventListener('click', () => setStockPage(1));
  // Sortable headers (name is the default; click Category to group by category).
  el.sortName && el.sortName.addEventListener('click', () => setStockSort('name'));
  el.sortCategory && el.sortCategory.addEventListener('click', () => setStockSort('category'));
  // Close any open kebab when clicking elsewhere.
  document.addEventListener('click', (ev) => {
    if (!ev.target.closest('.hr-menu-wrap')) closeKebabs();
  });
  el.itemForm && el.itemForm.addEventListener('submit', onCreateItem);
  el.receiveForm && el.receiveForm.addEventListener('submit', onReceive);
  el.receiveItem && el.receiveItem.addEventListener('change', applyReceiveTracking);
  el.issueForm && el.issueForm.addEventListener('submit', onIssue);
  el.issueSeg && el.issueSeg.addEventListener('click', (ev) => {
    const b = ev.target.closest('button[data-dest]');
    if (b && !b.disabled) setIssueDest(b.dataset.dest);
  });
  el.issueTarget && el.issueTarget.addEventListener('change', onIssueTargetChange);
  el.moveForm && el.moveForm.addEventListener('submit', onMove);
  el.moveFrom && el.moveFrom.addEventListener('change', updateAdjustDelta);
  el.moveQty && el.moveQty.addEventListener('input', updateAdjustDelta);
  el.itemClassSeg && el.itemClassSeg.addEventListener('click', (ev) => {
    const b = ev.target.closest('button[data-class]');
    if (b) setItemClass(b.dataset.class);
  });
  el.itemTracking && el.itemTracking.addEventListener('change', syncOpeningStockFields);
  el.itemCategoryNewBtn && el.itemCategoryNewBtn.addEventListener('click', () => openCategory());
}

export function init() {
  resolveStockDom();
  wireStock();
  ensureLookups().then(() => {
    fillSelect(el.categoryFilter, categories, { labelFn: c => c.name, placeholder: 'All categories' });
    fillSelect(el.warehouseFilter, locations, { labelFn: l => l.name, placeholder: 'All locations' });
  }).catch(() => {});
  loadStock();
}

async function loadStock() {
  if (!el.tbody) return;
  _stockPage = 0;  // a fresh query starts at the first page (reopen flows re-seek after)
  el.tbody.innerHTML = `<tr><td colspan="8" style="color:var(--sub);">Loading…</td></tr>`;
  const p = new URLSearchParams();
  if (el.search && el.search.value.trim()) p.set('search', el.search.value.trim());
  if (el.categoryFilter && el.categoryFilter.value) p.set('category_id', el.categoryFilter.value);
  if (el.warehouseFilter && el.warehouseFilter.value) p.set('warehouse_id', el.warehouseFilter.value);
  if (el.statusFilter && el.statusFilter.value) p.set('status', el.statusFilter.value);
  try {
    const data = await api.fetch(API.inventoryItems(p.toString()), { method: 'GET' });
    renderStock(data);
  } catch (err) {
    el.tbody.innerHTML = `<tr><td colspan="8" style="color:var(--err);">${escapeHtml(err.message || 'Failed to load')}</td></tr>`;
  }
}

// KPI value: emphasise the count, but dim it to neutral when zero (P4-2).
function setKpi(node, value) {
  if (!node) return;
  node.textContent = value ?? 0;
  node.classList.toggle('is-zero', !Number(value));
}

// The class-aware quick actions offered on a stock row's kebab. Mirrors
// detailActionsFor() so the ⋮ menu fires the same actions as the detail bar.
function actionsForItem(item) {
  const acts = [{ action: 'receive', label: 'Receive' }, { action: 'transfer', label: 'Transfer' }];
  const c = item.classification;
  if (c === 'customer' || c === 'consumable') acts.push({ action: 'issue', label: 'Issue' });
  if (c === 'infrastructure') acts.push({ action: 'deinstall', label: 'De-install' });
  if (c === 'tool') acts.push({ action: 'assign', label: 'Assign' }, { action: 'return', label: 'Return' });
  acts.push({ action: 'adjust', label: 'Adjust' }, { action: 'write_off', label: 'Write-off' }, { action: 'dispose', label: 'Dispose' });
  return acts;
}

const STOCK_PAGE_SIZE = 30;
let _stockRows = {};      // id → row on the current page, so the kebab can act without a detail fetch
let _stockAll = [];       // full filtered set from the server (KPIs are server-computed over this set)
let _stockPage = 0;       // zero-based current page
let _stockSort = 'name';  // 'name' | 'category' — default name
let _stockOpenId = null;  // id of the row whose detail is expanded inline (one at a time)
let _stockDetail = {};    // id → fetched detail payload, so a re-render keeps the open detail
let _stockLedger = {};    // id → movement ledger (movements + running balance) for the window
let _ledgerFrom = '';     // yyyy-mm-dd window start (default: 5 days ago)
let _ledgerTo = '';       // yyyy-mm-dd window end (default: today)

function _isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

// Default the movement window to the last 5 days (local dates).
function ensureLedgerRange() {
  if (_ledgerFrom && _ledgerTo) return;
  const now = new Date();
  _ledgerTo = _isoDate(now);
  _ledgerFrom = _isoDate(new Date(now.getTime() - 5 * 86400000));
}

// Fetch the movement ledger (with running balance) for the open item's window,
// then re-paint so the inline detail shows it. Non-fatal on error.
async function fetchLedger(itemId) {
  ensureLedgerRange();
  const p = new URLSearchParams();
  p.set('from_date', `${_ledgerFrom}T00:00:00`);
  p.set('to_date', `${_ledgerTo}T23:59:59`);
  try {
    const d = await api.fetch(API.inventoryItemLedger(itemId, p.toString()), { method: 'GET' });
    _stockLedger[itemId] = d;
    if (_stockOpenId === itemId) paintStock();
  } catch (err) {
    // Leave any prior ledger in place; the rest of the detail still renders.
  }
}

// The scrollable movement ledger: newest first, with the on-hand balance after
// each move and the opening balance for the window.
function stockLedgerHtml(itemId) {
  const led = _stockLedger[itemId];
  if (!led) return '<span style="color:var(--sub);">Loading movements…</span>';
  const entries = led.entries || [];
  if (!entries.length) {
    return `<div style="font-size:12px; color:var(--sub);">No movements in this period. Opening balance <strong>${fmt(led.opening_balance)}</strong>.</div>`;
  }
  const rows = entries.map((e) => {
    const ref = movementRef(e) || (e.note || '');
    const neg = Number(e.delta) < 0;
    const deltaTxt = `${Number(e.delta) > 0 ? '+' : ''}${fmt(e.delta)}`;
    return `<tr>
      <td style="white-space:nowrap;">${escapeHtml((e.occurred_at || '').slice(0, 10))}</td>
      <td>${movementBadge(e.movement_type)}</td>
      <td style="text-align:right; color:${neg ? 'var(--err)' : 'var(--ok, #16a34a)'};">${deltaTxt}</td>
      <td style="text-align:right;"><strong>${fmt(e.balance_after)}</strong></td>
      <td style="font-size:11px; color:var(--sub);">${escapeHtml(ref)}</td>
    </tr>`;
  }).join('');
  return `<div style="max-height:280px; overflow-y:auto; border:1px solid var(--border, #e5e7eb); border-radius:6px;">
    <table class="inv-mini-table" style="width:100%; font-size:12px;">
      <thead><tr>
        <th style="text-align:left;">Date</th><th style="text-align:left;">Type</th>
        <th style="text-align:right;">Δ</th><th style="text-align:right;">Balance</th><th style="text-align:left;">Ref</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div style="font-size:11px; color:var(--sub); padding:6px 8px; border-top:1px solid var(--border, #e5e7eb);">Opening balance <strong>${fmt(led.opening_balance)}</strong> · ${entries.length} movement${entries.length === 1 ? '' : 's'}</div>
  </div>`;
}

function renderStock(data) {
  const k = data.kpis || {};
  setKpi(el.kpiSkus, k.skus);
  setKpi(el.kpiLow, k.low);
  setKpi(el.kpiOut, k.out);
  setKpi(el.kpiReserved, k.reserved);
  // Outline whichever KPI card is the active quick-filter.
  const activeStatus = el.statusFilter ? el.statusFilter.value : '';
  [[el.kpiOut, 'out'], [el.kpiLow, 'low']].forEach(([node, val]) => {
    const card = node && node.closest('.inv-card');
    if (card) card.style.outline = activeStatus === val ? '2px solid var(--accent, #2563eb)' : '';
  });
  _stockAll = data.items || [];
  // Drop a stale open detail (item left the filtered set) and its cache.
  if (_stockOpenId && !_stockAll.some(i => i.id === _stockOpenId)) _stockOpenId = null;
  paintStock();
}

// Sort the full set by the active key; name is the tiebreaker so the order is
// stable. Category sorts by resolved category name, then by item name.
function sortedStock() {
  const arr = _stockAll.slice();
  const byName = (a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
  if (_stockSort === 'category') {
    arr.sort((a, b) =>
      (categoryName(a.category_id) || '').localeCompare(categoryName(b.category_id) || '', undefined, { sensitivity: 'base' })
      || byName(a, b));
  } else {
    arr.sort(byName);
  }
  return arr;
}

// Render the current page: a row per item, with the open item's detail expanded
// inline directly under it (see the Requests queue for the same pattern).
function paintStock() {
  if (!el.tbody) return;
  updateSortCarets();
  const rows = sortedStock();
  const pageCount = Math.max(1, Math.ceil(rows.length / STOCK_PAGE_SIZE));
  if (_stockPage > pageCount - 1) _stockPage = pageCount - 1;
  if (_stockPage < 0) _stockPage = 0;
  _stockRows = {};
  if (!rows.length) {
    el.tbody.innerHTML = `<tr><td colspan="8" style="color:var(--sub);">No items.</td></tr>`;
    renderStockPager(0, 0, 0);
    return;
  }
  const start = _stockPage * STOCK_PAGE_SIZE;
  const pageRows = rows.slice(start, start + STOCK_PAGE_SIZE);
  el.tbody.innerHTML = pageRows.map(i => {
    _stockRows[i.id] = i;
    const isOpen = _stockOpenId === i.id;
    if (isOpen) activeItem = _stockDetail[i.id]?.item || i;
    return stockRowHtml(i, isOpen) + (isOpen ? stockDetailRowHtml(i) : '');
  }).join('');
  renderStockPager(start, pageRows.length, rows.length);
}

function stockRowHtml(i, isOpen) {
  const cat = categoryName(i.category_id);
  const low = i.below_reorder ? ' style="text-align:right; color:var(--err); font-weight:600;"' : ' style="text-align:right;"';
  return `<tr class="inv-clickable-row${isOpen ? ' inv-row-open' : ''}" data-item-row="${escapeHtml(i.id)}">
      <td><strong>${escapeHtml(i.name)}</strong>${i.is_fixed_asset ? ' <span style="font-size:10px; color:var(--sub);">(fixed asset)</span>' : ''}<div style="font-size:11px; color:var(--sub);">${escapeHtml(i.sku)}</div></td>
      <td>${cat ? badge(cat, '') : '<span style="color:var(--sub);">—</span>'}</td>
      <td>${classBadge(i.classification)}</td>
      <td style="text-align:right;">${fmt(i.on_hand)}</td>
      <td style="text-align:right;">${fmt(i.reserved)}</td>
      <td${low}>${fmt(i.available)}</td>
      <td>${stockStatusBadge(i)}</td>
      ${kebabRow(i)}
    </tr>`;
}

// The inline detail row: rendered from the cached fetch; shows "Loading…" until
// openItemDetail's fetch resolves and re-paints.
function stockDetailRowHtml(i) {
  const d = _stockDetail[i.id];
  const inner = d
    ? stockDetailInner(d)
    : '<span style="color:var(--sub);">Loading…</span>';
  const item = (d && d.item) || i;
  const meta = `${classBadge(item.classification)} · ${escapeHtml(item.tracking || '')}`
    + (item.access_tech ? ' · ' + escapeHtml(item.access_tech) : '')
    + (item.is_fixed_asset ? ' · <span style="color:var(--sub);">fixed asset</span>' : '');
  return `<tr class="inv-inline-detail"><td colspan="8"><div class="inv-inline-box">
      <div class="inv-inline-head">
        <div><strong>${escapeHtml(item.sku)} — ${escapeHtml(item.name)}</strong><div class="inv-detail-sub" style="font-size:12px; color:var(--sub);">${meta}</div></div>
        <div style="display:flex; align-items:flex-start; gap:8px; flex-shrink:0;">
          <div class="inv-inline-actions" style="margin-top:0; justify-content:flex-end;">${detailActionsFor(item)}</div>
          <button type="button" class="btn ghost" data-stock-close title="Close">✕</button>
        </div>
      </div>
      ${inner}
    </div></td></tr>`;
}

function stockDetailInner(d) {
  const item = d.item;
  const byLoc = d.stock_by_location || [];
  const onHand = byLoc.reduce((s, x) => s + Number(x.on_hand || 0), 0);
  const reserved = byLoc.reduce((s, x) => s + Number(x.reserved || 0), 0);
  const available = onHand - reserved;
  const reorder = item.reorder_point;
  const below = reorder != null && available <= Number(reorder);
  const st = stockStatus({ available, below_reorder: below });
  const kv = [
    ['Status', badge(st.label, st.variant)],
    ['On hand', fmt(onHand)],
    ['Reserved', fmt(reserved)],
    ['Available', `<strong>${fmt(available)}</strong>`],
    ['Reorder point', reorder != null ? fmt(reorder) : '—'],
  ];
  if (item.is_fixed_asset && item.ownership_state) kv.push(['Ownership', titleCase(item.ownership_state)]);
  const kvHtml = kv.map(([k, v]) => `<div><span class="k">${escapeHtml(k)}</span><span class="v">${v}</span></div>`).join('');
  const stockHtml = byLoc.length
    ? `<table class="inv-mini-table"><tbody>${byLoc.map(s => `<tr><td>${escapeHtml(s.location_name)}</td><td style="text-align:right;"><strong>${fmt(s.on_hand)}</strong></td><td style="text-align:right; color:var(--sub);">${Number(s.reserved) ? fmt(s.reserved) + ' res.' : ''}</td></tr>`).join('')}</tbody></table>`
    : '<span style="color:var(--sub);">No stock on hand.</span>';
  ensureLedgerRange();
  // Movements is the prominent view (left, wider); stock-by-location — usually
  // only 3–4 rows — sits to the right.
  return `<div class="inv-kv">${kvHtml}</div>
    <div style="display:grid; grid-template-columns:2fr 1fr; gap:16px; align-items:start; margin-top:4px;">
      <div>
        <div style="display:flex; align-items:center; gap:8px; flex-wrap:wrap; margin-bottom:6px;">
          <div class="label" style="font-size:12px; color:var(--sub);">Movements &amp; balance</div>
          <span class="spacer" style="flex:1;"></span>
          <label style="font-size:11px; color:var(--sub); display:inline-flex; align-items:center; gap:4px;">From <input type="date" data-ledger-from value="${escapeHtml(_ledgerFrom)}" style="font-size:12px; padding:2px 4px;"></label>
          <label style="font-size:11px; color:var(--sub); display:inline-flex; align-items:center; gap:4px;">To <input type="date" data-ledger-to value="${escapeHtml(_ledgerTo)}" style="font-size:12px; padding:2px 4px;"></label>
        </div>
        ${stockLedgerHtml(item.id)}
      </div>
      <div>
        <div class="label" style="font-size:12px; color:var(--sub); margin-bottom:6px;">Stock by location</div>
        <div style="font-size:13px;">${stockHtml}</div>
      </div>
    </div>`;
}

function renderStockPager(start, shown, total) {
  if (el.pagerInfo) {
    el.pagerInfo.textContent = total
      ? `${start + 1}–${start + shown} of ${total}`
      : '0 items';
  }
  const pageCount = Math.max(1, Math.ceil(total / STOCK_PAGE_SIZE));
  if (el.pagerPrev) el.pagerPrev.disabled = _stockPage <= 0;
  if (el.pagerNext) el.pagerNext.disabled = _stockPage >= pageCount - 1;
}

function setStockPage(delta) {
  _stockPage += delta;
  paintStock();
}

function setStockSort(key) {
  if (key !== 'name' && key !== 'category') return;
  if (_stockSort === key) return;
  _stockSort = key;
  _stockPage = 0;
  paintStock();
}

function updateSortCarets() {
  const mark = (node, key) => { if (node) node.textContent = _stockSort === key ? '▲' : ''; };
  mark(el.sortCaretName, 'name');
  mark(el.sortCaretCategory, 'category');
}

// A kebab cell built from hr-ui's kebabMenu-style markup, namespaced `inv`.
function kebabRow(item) {
  const items = actionsForItem(item).map(a =>
    `<button type="button" role="menuitem" data-inv-action="${a.action}" data-inv-target="${escapeHtml(item.id)}">${escapeHtml(a.label)}</button>`
  ).join('');
  return `<td class="hr-menu-wrap">`
    + `<button type="button" class="btn ghost" data-inv-menu="${escapeHtml(item.id)}" aria-haspopup="menu" aria-expanded="false" title="Actions">⋮</button>`
    + `<div class="hr-menu" data-inv-menu-for="${escapeHtml(item.id)}" role="menu" hidden>${items}</div>`
    + `</td>`;
}

function closeKebabs() {
  if (!el.tbody) return;
  el.tbody.querySelectorAll('.hr-menu:not([hidden])').forEach(m => { m.hidden = true; });
  el.tbody.querySelectorAll('button[data-inv-menu][aria-expanded="true"]').forEach(b => b.setAttribute('aria-expanded', 'false'));
}

// Dispatch a class-aware action from the kebab or detail bar. Sets activeItem so
// the movement submit handlers act on the right item.
function runItemAction(item, action) {
  activeItem = item;
  if (action === 'edit') openEditItem(item);
  else if (action === 'receive') openReceive(item);
  else if (action === 'issue') openIssue(item);
  else openMove(item, action);
}

function locationName(id) {
  if (!id) return null;
  const pools = [locations, allLocations];
  for (const pool of pools) {
    const hit = (pool || []).find(l => l.id === id);
    if (hit) return hit.name;
  }
  return null;
}

// A movement's destination in words: prefer the human label, then the source
// ref, then resolved from/to location names.
function movementRef(m) {
  if (m.source_label) return m.source_label;
  if (m.source_ref) return m.source_ref;
  const from = m.from_location_name || locationName(m.from_location_id);
  const to = m.to_location_name || locationName(m.to_location_id);
  if (from && to) return `${from} → ${to}`;
  return to || from || '';
}

// Open a row's detail inline. Marks the row open (paints a "Loading…" detail if
// the item is on the current page), then fetches and caches the detail and
// re-paints. Also makes sure the item's page is the one on screen.
async function openItemDetail(itemId) {
  _stockOpenId = itemId;
  // Jump to the page holding this item (usually already current).
  const idx = sortedStock().findIndex(i => i.id === itemId);
  if (idx >= 0) _stockPage = Math.floor(idx / STOCK_PAGE_SIZE);
  paintStock();
  fetchLedger(itemId);  // movements + running balance (re-paints when it lands)
  try {
    const d = await api.fetch(API.inventoryItem(itemId), { method: 'GET' });
    _stockDetail[itemId] = d;
    if (_stockOpenId === itemId) {
      activeItem = d.item;
      paintStock();
    }
  } catch (err) {
    flash(err.message || 'Failed to load item', 'error');
  }
}

function closeItemDetail() {
  _stockOpenId = null;
  activeItem = null;
  paintStock();
}

function toggleItemDetail(itemId) {
  if (_stockOpenId === itemId) closeItemDetail();
  else openItemDetail(itemId);
}

function act(type, label) { return `<button class="btn" data-action="${type}">${label}</button>`; }

function detailActionsFor(item) {
  const b = [act('receive', 'Receive'), act('transfer', 'Transfer')];
  const c = item.classification;
  if (c === 'customer' || c === 'consumable') b.push(act('issue', 'Issue'));
  if (c === 'infrastructure') b.push(act('deinstall', 'De-install'));
  if (c === 'tool') b.push(act('assign', 'Assign'), act('return', 'Return'));
  b.push(act('adjust', 'Adjust'), act('write_off', 'Write-off'), act('dispose', 'Dispose'));
  b.push(`<button class="btn ghost" data-action="edit" title="Edit this item's details (fix a mistake)">✎ Edit</button>`);
  return b.join(' ');
}

// --- New item ---

// ItemCategoryGroup value → friendly label. Groups are a fixed shortlist the
// category rows hang under; used both to group the New-item dropdown and to
// label the Group column in category management.
const GROUP_LABEL = {
  network_customer: 'Network — customer', network_infra: 'Network — infrastructure',
  power_site: 'Power & site', fixtures_hardware: 'Fixtures & hardware',
  fabrication_materials: 'Fabrication materials', cable_consumables: 'Cable & consumables',
  tools_test_equipment: 'Tools & test equipment', it: 'IT equipment', vehicles: 'Vehicles',
};
const CATEGORY_FAMILY = GROUP_LABEL;

function fillCategorySelect(sel) {
  if (!sel) return;
  // Group by the category's family field (group_name) when present.
  const groups = new Map();
  let flat = false;
  for (const c of categories) {
    const key = c.group_name || c.family || null;
    if (!key) { flat = true; }
    const label = CATEGORY_FAMILY[key] || (key ? titleCase(key) : 'Other');
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(c);
  }
  if (flat && groups.size <= 1) {
    fillSelect(sel, categories, { labelFn: c => c.name, placeholder: 'Select…' });
    return;
  }
  const opts = ['<option value="">Select…</option>'];
  for (const [label, items] of groups) {
    opts.push(`<optgroup label="${escapeHtml(label)}">`);
    for (const c of items) opts.push(`<option value="${escapeHtml(c.id)}">${escapeHtml(c.name)}</option>`);
    opts.push('</optgroup>');
  }
  sel.innerHTML = opts.join('');
}

function setItemClass(cls) {
  el.itemClassification.value = cls;
  el.itemClassSeg.querySelectorAll('button[data-class]').forEach(b =>
    b.classList.toggle('active', b.dataset.class === cls)
  );
  const m = CLASS_META[cls];
  el.itemClassHint.textContent = m ? m.hint : '';
}

// Fixed-asset creation lives on the Assets view (opened with {fixedAsset:true});
// plain stock creation from the Stock view leaves this false.
let _newItemFixedAsset = false;
// Set to an item id when the dialog is reused to EDIT an existing item (fix a
// mistake in sku/name/category/classification/unit/tracking) rather than create.
let _editingItemId = null;

// The new item's effective tracking (fixed assets are always serialized).
function newItemIsSerialized() {
  return _newItemFixedAsset || (el.itemTracking && el.itemTracking.value === 'serialized');
}

// A fixed asset is, by definition, serial-tracked (backend enforces it). In
// fixed-asset mode, force Tracking to Serialized and lock it. Also swap the
// opening-stock input: a serialized item takes serials (one unit each), not a qty.
function syncFixedAssetTracking() {
  if (!el.itemTracking) return;
  if (_newItemFixedAsset) {
    el.itemTracking.value = 'serialized';
    el.itemTracking.disabled = true;
  } else {
    el.itemTracking.disabled = false;
  }
  syncOpeningStockFields();
}

function syncOpeningStockFields() {
  const serialized = newItemIsSerialized();
  if (el.itemOpeningQtyField) el.itemOpeningQtyField.hidden = serialized;
  if (el.itemOpeningSerialsField) el.itemOpeningSerialsField.hidden = !serialized;
}

// opts.fixedAsset: create a fixed asset (Assets view) rather than plain stock.
function openNewItem(opts = {}) {
  if (!el.itemDlg) return;
  _newItemFixedAsset = !!opts.fixedAsset;
  _editingItemId = null;
  ensureLookups().then(() => {
    el.itemForm.reset();
    fillCategorySelect(el.itemCategory);
    fillSelect(el.itemOpeningLocation, locations.filter(l => l.kind !== 'quarantine'), { labelFn: l => l.name, placeholder: '—' });
    el.itemUnit.value = 'each';
    // Create mode: opening-stock section on, SKU/tracking enabled, submit = create.
    if (el.itemOpeningSection) el.itemOpeningSection.hidden = false;
    if (el.itemSku) el.itemSku.disabled = false;
    if (el.itemEditNote) el.itemEditNote.hidden = true;
    if (el.itemSubmit) el.itemSubmit.textContent = _newItemFixedAsset ? 'Create fixed asset' : 'Create item';
    if (el.itemDlgTitle) el.itemDlgTitle.textContent = _newItemFixedAsset ? 'New fixed asset' : 'New item';
    if (el.itemFixedAssetNote) el.itemFixedAssetNote.hidden = !_newItemFixedAsset;
    // A fixed asset can only be infrastructure or a tool — hide the customer /
    // consumable options. And reorder point (added one unit at a time) and access
    // tech (a building or vehicle has none) don't apply to a fixed asset.
    el.itemClassSeg.querySelectorAll('button[data-class]').forEach(b => {
      const assetOnly = b.dataset.class === 'customer' || b.dataset.class === 'consumable';
      b.hidden = _newItemFixedAsset && assetOnly;
    });
    if (el.itemReorderField) el.itemReorderField.hidden = _newItemFixedAsset;
    if (el.itemAccessTechField) el.itemAccessTechField.hidden = _newItemFixedAsset;
    setItemClass(_newItemFixedAsset ? 'infrastructure' : 'customer');
    syncFixedAssetTracking();  // locks serialized in fixed-asset mode; re-enables otherwise
    openDialog(el.itemDlg);
  }).catch(() => flash('Failed to load categories', 'error'));
}

function openNewFixedAsset() {
  openNewItem({ fixedAsset: true });
}

// Reuse the item dialog to EDIT an existing item — fix a mistake in its
// sku / name / category / classification / unit / tracking. Tracking is locked
// once the item holds stock (and always for a fixed asset); the backend enforces
// the same. Opening-stock inputs are hidden (an edit never receives stock).
function openEditItem(item, opts = {}) {
  if (!el.itemDlg || !item) return;
  _newItemFixedAsset = !!item.is_fixed_asset;
  _editingItemId = item.id;
  ensureLookups().then(() => {
    el.itemForm.reset();
    fillCategorySelect(el.itemCategory);
    el.itemSku.value = item.sku || '';
    el.itemName.value = item.name || '';
    el.itemCategory.value = item.category_id || '';
    el.itemUnit.value = item.unit || 'each';
    if (el.itemTracking) el.itemTracking.value = item.tracking || 'bulk';
    if (el.itemReorder) el.itemReorder.value = item.reorder_point != null ? item.reorder_point : '';
    if (el.itemAccessTech) el.itemAccessTech.value = item.access_tech || '';
    if (el.itemDlgTitle) el.itemDlgTitle.textContent = _newItemFixedAsset ? 'Edit fixed asset' : 'Edit item';
    if (el.itemFixedAssetNote) el.itemFixedAssetNote.hidden = true;
    if (el.itemSubmit) el.itemSubmit.textContent = 'Save changes';
    if (el.itemOpeningSection) el.itemOpeningSection.hidden = true;   // no receiving on an edit
    if (el.itemSku) el.itemSku.disabled = false;
    // Class control: hide customer/consumable options for a fixed asset.
    el.itemClassSeg.querySelectorAll('button[data-class]').forEach(b => {
      const assetOnly = b.dataset.class === 'customer' || b.dataset.class === 'consumable';
      b.hidden = _newItemFixedAsset && assetOnly;
    });
    if (el.itemReorderField) el.itemReorderField.hidden = _newItemFixedAsset;
    if (el.itemAccessTechField) el.itemAccessTechField.hidden = _newItemFixedAsset;
    setItemClass(item.classification || 'customer');
    // Lock tracking for a fixed asset or once stock exists (caller may pass a
    // hint; otherwise infer from the current stock-list row).
    const row = _stockRows[item.id] || {};
    const hasStock = opts.hasStock != null
      ? !!opts.hasStock
      : (Number(row.on_hand || 0) !== 0 || Number(row.reserved || 0) !== 0);
    const lockTracking = _newItemFixedAsset || hasStock;
    if (el.itemTracking) el.itemTracking.disabled = lockTracking;
    if (el.itemEditNote) {
      if (_newItemFixedAsset) el.itemEditNote.textContent = 'A fixed asset is always serial-tracked.';
      else if (hasStock) el.itemEditNote.textContent = 'Tracking is locked because this item already has stock — every other field is still editable.';
      el.itemEditNote.hidden = !lockTracking;
    }
    openDialog(el.itemDlg);
  }).catch(() => flash('Failed to load categories', 'error'));
}

async function onSaveItemEdit() {
  const id = _editingItemId;
  if (!id) return;
  const body = {
    sku: el.itemSku.value.trim(),
    name: el.itemName.value.trim(),
    category_id: el.itemCategory.value,
    classification: el.itemClassification.value,
    unit: el.itemUnit.value.trim() || 'each',
  };
  if (!body.sku || !body.name || !body.category_id) {
    flash('SKU, name and category are required.', 'error');
    return;
  }
  // Only send tracking when it's still editable (not locked) — avoids a needless guard trip.
  if (el.itemTracking && !el.itemTracking.disabled) body.tracking = el.itemTracking.value;
  if (!_newItemFixedAsset && el.itemReorder && el.itemReorder.value) body.reorder_point = el.itemReorder.value;
  if (!_newItemFixedAsset && el.itemAccessTech && el.itemAccessTech.value) body.access_tech = el.itemAccessTech.value;
  try {
    await api.fetch(API.inventoryItem(id), { method: 'PATCH', body: JSON.stringify(body) });
    closeDialog(el.itemDlg);
    flash('Item updated', 'success');
    _editingItemId = null;
    if (_newItemFixedAsset && assetsDom) loadAssets();
    await loadStock();
    openItemDetail(id);   // reopen the inline detail with fresh values
  } catch (err) {
    flash(err.message || 'Failed to update item', 'error');
  }
}

async function onCreateItem(ev) {
  ev.preventDefault();
  if (_editingItemId) return void onSaveItemEdit();
  const serialized = newItemIsSerialized();
  // Opening stock: a serialized item takes serials (one unit each); a bulk item a qty.
  const openingSerials = serialized
    ? [...new Set((el.itemOpeningSerials.value || '').split('\n').map(s => s.trim()).filter(Boolean))]
    : [];
  const openingQty = (!serialized && el.itemOpeningQty.value && Number(el.itemOpeningQty.value) > 0)
    ? el.itemOpeningQty.value : null;
  const hasOpening = serialized ? openingSerials.length > 0 : !!openingQty;
  const openingFile = el.itemOpeningFile && el.itemOpeningFile.files && el.itemOpeningFile.files[0];
  if (hasOpening && (!el.itemOpeningLocation.value || !openingFile)) {
    flash('Opening stock needs a location and a document.', 'error');
    return;
  }
  const body = {
    sku: el.itemSku.value.trim(), name: el.itemName.value.trim(),
    category_id: el.itemCategory.value, classification: el.itemClassification.value,
    // A fixed asset is always serial-tracked — force it regardless of the (locked) select.
    tracking: serialized ? 'serialized' : el.itemTracking.value,
    unit: el.itemUnit.value.trim() || 'each',
    is_fixed_asset: _newItemFixedAsset,
  };
  // Reorder point + access tech don't apply to a fixed asset.
  if (!_newItemFixedAsset && el.itemReorder.value) body.reorder_point = el.itemReorder.value;
  if (!_newItemFixedAsset && el.itemAccessTech.value) body.access_tech = el.itemAccessTech.value;
  try {
    const created = await api.fetch(API.inventoryItems(), { method: 'POST', body: JSON.stringify(body) });
    // Optional opening stock → upload one document, then post the receipt(s).
    if (hasOpening) {
      const fd = new FormData();
      fd.append('file', openingFile);
      fd.append('doc_type', 'internal_voucher');
      fd.append('reference', 'Opening stock');
      const doc = await api.fetch(API.inventoryDocumentUpload(), { method: 'POST', body: fd });
      const loc = el.itemOpeningLocation.value;
      if (serialized) {
        // One receive per serial (each quantity 1, per the ledger rule).
        for (const serial of openingSerials) {
          await api.fetch(API.inventoryMovementReceive(), {
            method: 'POST',
            body: JSON.stringify({ item_id: created.id, to_location_id: loc, document_id: doc.id, quantity: '1', serial }),
          });
        }
      } else {
        await api.fetch(API.inventoryMovementReceive(), {
          method: 'POST',
          body: JSON.stringify({ item_id: created.id, to_location_id: loc, document_id: doc.id, quantity: openingQty }),
        });
      }
    }
    closeDialog(el.itemDlg);
    const noun = _newItemFixedAsset ? 'Fixed asset' : 'Item';
    flash(hasOpening ? `${noun} created with opening stock` : `${noun} created`, 'success');
    if (_newItemFixedAsset && assetsDom) loadAssets();
    loadStock();
  } catch (err) {
    flash(err.message || 'Failed to create item', 'error');
  }
}

// --- Receive ---

let _receiveAssetMode = false;

// The tracking of whatever item the receive dialog currently targets — the preset
// item, or the one chosen in the top-level picker.
function receiveTargetTracking() {
  if (el.receiveItemPickField && !el.receiveItemPickField.hidden) {
    const sel = (_itemsCache || []).find(i => i.id === el.receiveItem.value);
    return sel ? sel.tracking : null;
  }
  return activeItem ? activeItem.tracking : null;
}

// Serialized items are received one identified unit at a time (ledger rule), so
// swap the Quantity field for a one-serial-per-line box; bulk items keep Quantity.
function applyReceiveTracking() {
  const serialized = receiveTargetTracking() === 'serialized';
  if (el.receiveQtyField) el.receiveQtyField.hidden = serialized;
  if (el.receiveSerialsField) el.receiveSerialsField.hidden = !serialized;
  // Don't leave a hidden field marked required (blocks submit).
  if (el.receiveQty) el.receiveQty.required = !serialized;
}

// openReceive(null) opens the top-level flow with an item picker; openReceive(item)
// receives against a known item. opts.asset registers serialized assets — an asset
// is a serial-tracked unit, entered by being received against a serial.
function openReceive(item, opts = {}) {
  _receiveAssetMode = !!opts.asset;
  Promise.all([ensureLookups(), ensureItemsCache()]).then(() => {
    el.receiveForm.reset();
    const topLevel = !item || _receiveAssetMode;
    if (el.receiveTitle) el.receiveTitle.textContent = _receiveAssetMode ? 'Register asset' : 'Receive stock';
    if (el.receiveItemField) el.receiveItemField.hidden = topLevel;
    if (el.receiveItemPickField) el.receiveItemPickField.hidden = !topLevel;
    if (topLevel) {
      activeItem = null;
      // Asset registration only makes sense for serialized items.
      const pool = _receiveAssetMode
        ? (_itemsCache || []).filter(i => i.tracking === 'serialized')
        : (_itemsCache || []);
      fillSelect(el.receiveItem, pool, { labelFn: i => `${i.sku} — ${i.name}`, placeholder: 'Select item…' });
    } else {
      activeItem = item;
      el.receiveItemName.value = `${item.sku} — ${item.name}`;
    }
    fillSelect(el.receiveLocation, locations.filter(l => l.kind !== 'quarantine'), { labelFn: l => l.name, placeholder: 'Select…' });
    el.receiveQty.value = '1';
    applyReceiveTracking();
    openDialog(el.receiveDlg);
  }).catch(() => flash('Failed to load lookups', 'error'));
}

function openRegisterAsset() {
  openReceive(null, { asset: true });
}

async function onReceive(ev) {
  ev.preventDefault();
  // Resolve the target item — from the picker (top-level) or the active item.
  const topLevel = el.receiveItemPickField && !el.receiveItemPickField.hidden;
  const itemId = topLevel ? el.receiveItem.value : (activeItem && activeItem.id);
  if (!itemId) { flash('Pick an item to receive', 'error'); return; }
  const serialized = receiveTargetTracking() === 'serialized';
  const docType = el.receiveDocType.value;
  const ref = el.receiveDocRef.value.trim();
  const file = el.receiveFile && el.receiveFile.files && el.receiveFile.files[0];
  if (!file) { flash('Attach the document — Finance needs a copy.', 'error'); return; }

  // For a serialized item, each line is one unit's serial; parse + dedupe.
  let serials = [];
  if (serialized) {
    serials = [...new Set((el.receiveSerials.value || '').split('\n').map(s => s.trim()).filter(Boolean))];
    if (!serials.length) { flash('Enter at least one serial (one per line).', 'error'); return; }
  }
  const unitCost = el.receiveUnitCost.value || null;
  const locId = el.receiveLocation.value;

  try {
    // 1) upload the supporting document once — every unit shares this one GRN.
    const fd = new FormData();
    fd.append('file', file);
    fd.append('doc_type', docType);
    if (ref) fd.append('reference', ref);
    const doc = await api.fetch(API.inventoryDocumentUpload(), { method: 'POST', body: fd });

    // 2) post the receipt(s).
    if (serialized) {
      // One receive per serial (each is quantity 1, per the ledger rule).
      let ok = 0; const failed = [];
      for (const serial of serials) {
        const body = { item_id: itemId, to_location_id: locId, document_id: doc.id, quantity: '1', serial };
        if (unitCost) body.unit_cost = unitCost;
        try { await api.fetch(API.inventoryMovementReceive(), { method: 'POST', body: JSON.stringify(body) }); ok++; }
        catch { failed.push(serial); }
      }
      closeDialog(el.receiveDlg);
      const noun = _receiveAssetMode ? 'asset' : 'unit';
      if (ok) flash(`Received ${ok} ${noun}${ok === 1 ? '' : 's'}${failed.length ? ` — ${failed.length} failed (duplicate serial?)` : ''}`, failed.length ? 'error' : 'success');
      else flash(`No units received — ${failed.length} failed (duplicate serial?)`, 'error');
    } else {
      const body = { item_id: itemId, to_location_id: locId, document_id: doc.id, quantity: el.receiveQty.value };
      if (unitCost) body.unit_cost = unitCost;
      await api.fetch(API.inventoryMovementReceive(), { method: 'POST', body: JSON.stringify(body) });
      closeDialog(el.receiveDlg);
      flash('Stock received', 'success');
    }
    if (_receiveAssetMode) { if (assetsDom) loadAssets(); }
    else { await loadStock(); openItemDetail(itemId); }
  } catch (err) {
    flash(err.message || 'Receive failed', 'error');
  }
}

// --- Issue ---

let _issueTargets = [];  // cached list from the issue-targets picker

function setIssueDest(kind) {
  el.issueDestKind.value = kind;
  el.issueSeg.querySelectorAll('button[data-dest]').forEach(b =>
    b.classList.toggle('active', b.dataset.dest === kind)
  );
  if (el.issueTargetField) el.issueTargetField.hidden = kind !== 'ticket';
  if (el.issueTechField) el.issueTechField.hidden = kind !== 'technician';
  if (el.issueWorkOrderNote) el.issueWorkOrderNote.hidden = kind !== 'work_order';
  el.issueRecordedUnder.textContent = '';
}

function onIssueTargetChange() {
  const t = _issueTargets.find(x => x.ref === el.issueTarget.value);
  el.issueRecordedUnder.textContent = t && t.customer ? `↳ Recorded under ${t.customer}` : '';
}

async function openIssue(item) {
  activeItem = item;
  try {
    await ensureLookups();
    el.issueForm.reset();
    el.issueItemName.value = `${item.sku} — ${item.name}`;
    fillSelect(el.issueLocation, locations, { labelFn: l => l.name, placeholder: 'Select…' });
    setIssueDest('ticket');
    // Populate the two pickers in parallel; a failure leaves an empty list, not a broken dialog.
    const [targets, techs] = await Promise.all([
      api.fetch(API.inventoryIssueTargets(), { method: 'GET' }).catch(() => ({ items: [] })),
      api.fetch(API.inventoryTechnicianTargets(), { method: 'GET' }).catch(() => ({ items: [] })),
    ]);
    _issueTargets = targets.items || [];
    fillSelect(el.issueTarget, _issueTargets, { valueKey: 'ref', labelFn: t => t.label, placeholder: 'Select…' });
    fillSelect(el.issueTech, techs.items || [], { valueKey: 'ref', labelFn: t => t.label, placeholder: 'Select…' });
    // stash technician display for the label
    (techs.items || []).forEach(t => { t._label = t.label; });
    el.issueTech._techs = techs.items || [];
    openDialog(el.issueDlg);
  } catch (err) {
    flash(err.message || 'Failed to open issue', 'error');
  }
}

async function onIssue(ev) {
  ev.preventDefault();
  if (!activeItem) return;
  const kind = el.issueDestKind.value;
  const body = {
    item_id: activeItem.id, from_location_id: el.issueLocation.value, quantity: el.issueQty.value,
  };
  if (kind === 'ticket') {
    const t = _issueTargets.find(x => x.ref === el.issueTarget.value);
    if (!t) { flash('Pick an active ticket or installation', 'error'); return; }
    body.ticket_ref = t.ref;
    body.source_label = t.label;
    if (t.support_customer_id) body.support_customer_id = t.support_customer_id;
  } else if (kind === 'technician') {
    const techs = el.issueTech._techs || [];
    const tech = techs.find(x => x.ref === el.issueTech.value);
    if (!tech) { flash('Pick a technician', 'error'); return; }
    body.ticket_ref = tech.ref;
    body.source_label = `${tech.label} (van custody)`;
  } else {
    flash('Work-order issue is not available yet', 'error');
    return;
  }
  try {
    if (el.issueSerial.value.trim()) body.stock_unit_id = await resolveUnitId(el.issueSerial.value.trim());
    await api.fetch(API.inventoryMovementIssue(), { method: 'POST', body: JSON.stringify(body) });
    closeDialog(el.issueDlg);
    flash('Stock issued', 'success');
    await loadStock();
    openItemDetail(activeItem.id);
  } catch (err) {
    flash(err.message || 'Issue failed', 'error');
  }
}

async function onScan() {
  const code = window.prompt('Scan or enter a code (SKU / serial / label):');
  if (!code) return;
  try {
    const r = await api.fetch(API.inventoryScan('code=' + encodeURIComponent(code)), { method: 'GET' });
    // Clear filters so the scanned item is in the list, then expand it inline.
    if (el.search) el.search.value = '';
    if (el.categoryFilter) el.categoryFilter.value = '';
    if (el.statusFilter) el.statusFilter.value = 'active';
    await loadStock();
    openItemDetail(r.item_id);
  } catch (err) {
    flash(err.message || 'Nothing matched that code', 'error');
  }
}

// ===========================================================================
// REQUESTS VIEW
// ===========================================================================

let reqDom = false;
let rel = {};
let activeRequest = null;
let issDom = false;
let iss = {};

// The two request queues both expand their detail INLINE (a row under the one
// clicked). `dom` is a getter because `rel`/`iss` are (re)assigned when their DOM
// is resolved. `render` re-draws the queue (used on close/reload).
const requestsView = {
  get dom() { return rel; }, reload: () => loadRequests(),
  open: (id) => openRequestDetail(id), render: () => renderRequestRows(),
};
const issuancesView = {
  get dom() { return iss; }, reload: () => loadIssuances(),
  open: (id) => { iss.openId = id; renderIssuanceRows(); }, render: () => renderIssuanceRows(),
};

function resolveReqDom() {
  if (reqDom) return;
  reqDom = true;
  const g = (id) => document.getElementById(id);
  rel = {
    statusFilter: g('invReqStatusFilter'), search: g('invReqSearch'),
    refreshBtn: g('invReqRefreshBtn'), tbody: g('invReqTbody'),
    pager: g('invReqPager'), offset: 0, openId: null, items: [],
  };
}

let reqWired = false;

function wireReq() {
  if (reqWired) return;
  reqWired = true;
  rel.statusFilter && rel.statusFilter.addEventListener('change', () => { rel.offset = 0; loadRequests(); });
  rel.refreshBtn && rel.refreshBtn.addEventListener('click', () => { rel.offset = 0; loadRequests(); });
  // A new search starts at the first page; debounced so typing does not spam the API.
  rel.search && rel.search.addEventListener('input', debounce(() => { rel.offset = 0; loadRequests(); }, 250));
  wirePager(rel.pager, (dir) => pagerStep(rel, dir, loadRequests));
  // The detail is inline (a row under the clicked one), so all of open/close,
  // request actions and per-line actions are delegated off the table body.
  rel.tbody && rel.tbody.addEventListener('click', (ev) => {
    const openBtn = ev.target.closest('button[data-req-open]');
    if (openBtn) { toggleRequestRow(openBtn.dataset.reqOpen); return; }
    if (ev.target.closest('button[data-req-action]')) { onRequestAction(ev); return; }
    if (ev.target.closest('button[data-assign]') || ev.target.closest('button[data-fulfil]')) { onLineAction(ev); return; }
  });
}

export function initRequests() {
  resolveReqDom();
  wireReq();
  rel.offset = 0;
  ensureLookups().catch(() => {});
  ensureItemsCache();
  loadRequests();
}

// ---------------------------------------------------------------------------
// Issuances queue — approved requests awaiting the storekeeper's hand-over
// (print the work order, then mark delivered). A request drops off once it is
// delivered. Reuses the shared request detail/actions renderers.
// ---------------------------------------------------------------------------

function resolveIssDom() {
  if (issDom) return;
  issDom = true;
  const g = (id) => document.getElementById(id);
  iss = {
    refreshBtn: g('invIssRefreshBtn'), search: g('invIssSearch'),
    tbody: g('invIssTbody'), pager: g('invIssPager'),
    offset: 0, openId: null, items: [],
  };
}

let issWired = false;
function wireIss() {
  if (issWired) return;
  issWired = true;
  iss.refreshBtn && iss.refreshBtn.addEventListener('click', () => { iss.offset = 0; loadIssuances(); });
  // A new search starts at the first page; debounced so typing does not spam the API.
  iss.search && iss.search.addEventListener('input', debounce(() => { iss.offset = 0; loadIssuances(); }, 250));
  wirePager(iss.pager, (dir) => pagerStep(iss, dir, loadIssuances));
  // Inline detail (like Requests): open/close, request actions and per-line
  // actions are all delegated off the table body.
  iss.tbody && iss.tbody.addEventListener('click', (ev) => {
    const openBtn = ev.target.closest('button[data-iss-open]');
    if (openBtn) { toggleIssuanceRow(openBtn.dataset.issOpen); return; }
    if (ev.target.closest('button[data-req-action]')) { onRequestAction(ev); return; }
    if (ev.target.closest('button[data-assign]') || ev.target.closest('button[data-fulfil]')) { onLineAction(ev); return; }
  });
}

export function initIssuances() {
  resolveIssDom();
  wireIss();
  iss.offset = 0;
  ensureLookups().catch(() => {});
  ensureItemsCache();
  loadIssuances();
}

async function loadIssuances() {
  if (!iss.tbody) return;
  iss.tbody.innerHTML = `<tr><td colspan="4" style="color:var(--sub);">Loading…</td></tr>`;
  const p = new URLSearchParams();
  ['approved', 'partially_approved', 'reserved'].forEach(st => p.append('status', st));
  if (iss.search && iss.search.value.trim()) p.set('q', iss.search.value.trim());
  p.set('limit', String(INV_PAGE_SIZE));
  p.set('offset', String(iss.offset || 0));
  try {
    const data = await api.fetch(API.inventoryStockRequests(p.toString()), { method: 'GET' });
    iss.items = data.items || [];
    renderPager(iss.pager, iss.offset || 0, data.count || 0);
    renderIssuanceRows();
  } catch (err) {
    if (iss.pager) iss.pager.innerHTML = '';
    iss.tbody.innerHTML = `<tr><td colspan="4" style="color:var(--err);">${escapeHtml(err.message || 'Failed to load')}</td></tr>`;
  }
}

async function loadRequests() {
  if (!rel.tbody) return;
  rel.tbody.innerHTML = `<tr><td colspan="5" style="color:var(--sub);">Loading…</td></tr>`;
  const p = new URLSearchParams();
  if (rel.statusFilter && rel.statusFilter.value) p.set('status', rel.statusFilter.value);
  if (rel.search && rel.search.value.trim()) p.set('q', rel.search.value.trim());
  p.set('limit', String(INV_PAGE_SIZE));
  p.set('offset', String(rel.offset || 0));
  try {
    const data = await api.fetch(API.inventoryStockRequests(p.toString()), { method: 'GET' });
    rel.items = data.items || [];
    renderPager(rel.pager, rel.offset || 0, data.count || 0);
    renderRequestRows();
  } catch (err) {
    if (rel.pager) rel.pager.innerHTML = '';
    rel.tbody.innerHTML = `<tr><td colspan="5" style="color:var(--err);">${escapeHtml(err.message || 'Failed to load')}</td></tr>`;
  }
}

function statusBadge(status) {
  return badge(titleCase(status || ''), REQ_BADGE[status] || '');
}

// Who/what a request is for, in human terms — resolved server-side from the
// producer (customer name for an install/ticket, WO ref for a work order),
// falling back to the cached label / raw ref.
function requestPrimary(r) {
  const meta = r.meta || {};
  return r.display_title || meta.customer_name || r.source_label || r.source_ref || '—';
}
// The secondary line that helps the approver relate to it (ticket code, or a
// work order's facility / asset).
function requestSubtitle(r) {
  if (r.display_subtitle) return r.display_subtitle;
  const code = (r.meta || {}).ticket_short_code;
  return code ? `Ticket ${code}` : '';
}
function requestRefHtml(r) {
  const who = escapeHtml(requestPrimary(r));
  const bits = [];
  const sub = requestSubtitle(r);
  if (sub) bits.push(escapeHtml(sub));
  if (r.requester_name) bits.push(`requested by ${escapeHtml(r.requester_name)}`);
  const line = bits.length ? `<div style="color:var(--sub); font-size:12px;">${bits.join(' · ')}</div>` : '';
  return `${who}${line}`;
}

function poStatusBadge(status) {
  return badge(titleCase(status || ''), PO_BADGE[status] || '');
}

// Both the Requests queue and the Issuances queue open a request into their own
// detail panel; `activeReqView` tracks which one is live so the shared action
// handlers refresh and re-open the right one.
let activeReqView = null;


// The Requests tab expands its detail INLINE, as a row directly under the one
// clicked (easier to scan than a panel at the bottom of the list). Only one row
// is open at a time; `rel.openId` tracks it. List items already carry their full
// lines, so no extra fetch is needed to render — a mutating action just reloads.
function openRequestDetail(id) {
  rel.openId = id;
  if ((rel.items || []).some(r => r.id === id)) renderRequestRows();
  else loadRequests();
}

function toggleRequestRow(id) {
  if (rel.openId === id) { rel.openId = null; activeRequest = null; renderRequestRows(); }
  else openRequestDetail(id);
}

function requestRowHtml(r, isOpen) {
  return `<tr data-req-row="${escapeHtml(r.id)}"${isOpen ? ' class="inv-row-open"' : ''}>
    <td>${escapeHtml(r.source_type)}</td>
    <td>${requestRefHtml(r)}</td>
    <td>${statusBadge(r.status)}</td>
    <td style="text-align:center;">${(r.lines || []).length}</td>
    <td><button class="btn" data-req-open="${escapeHtml(r.id)}">${isOpen ? '✕ Close' : 'Open'}</button></td>
  </tr>`;
}

function requestDetailRowHtml(r, colspan = 5) {
  const meta = `${escapeHtml(r.source_type)} · ${statusBadge(r.status)}`
    + (requestSubtitle(r) ? ` · <span style="color:var(--sub);">${escapeHtml(requestSubtitle(r))}</span>` : '')
    + (r.requester_name ? ` · <span style="color:var(--sub);">requested by ${escapeHtml(r.requester_name)}</span>` : '')
    + (r.delivery_note_number ? ` · <span style="color:var(--sub);">Note ${escapeHtml(r.delivery_note_number)}${r.delivered_at ? ' · delivered ' + escapeHtml((r.delivered_at || '').slice(0, 10)) : ''}</span>` : '');
  const lines = (r.lines || []).map(l => renderReqLine(l)).join('') || '<span style="color:var(--sub);">No lines.</span>';
  return `<tr class="inv-inline-detail"><td colspan="${colspan}"><div class="inv-inline-box">
      <div class="inv-inline-head">
        <div><strong>${escapeHtml(requestPrimary(r))}</strong><div class="inv-detail-sub" style="font-size:12px; color:var(--sub);">${meta}</div></div>
        <button class="btn ghost" data-req-action="close" title="Close">✕</button>
      </div>
      <div>${lines}</div>
      <div class="inv-inline-actions">${requestActionsFor(r, { closeButton: false })}</div>
    </div></td></tr>`;
}

// Generic inline queue renderer: a collapsed row per item, and — for the one
// open row — a detail row directly under it. Shared by the Requests and
// Issuances queues so both expand in place (only one open at a time).
function renderInlineQueue(view, { rowHtml, colspan, emptyText }) {
  const st = view.dom;
  if (!st.tbody) return;
  const items = st.items || [];
  if (!items.length) {
    st.tbody.innerHTML = `<tr><td colspan="${colspan}" style="color:var(--sub);">${emptyText}</td></tr>`;
    activeRequest = null;
    return;
  }
  // Drop a stale openId (e.g. the row left the list after a status filter/reload).
  if (st.openId && !items.some(r => r.id === st.openId)) st.openId = null;
  activeRequest = null;
  st.tbody.innerHTML = items.map(r => {
    const isOpen = st.openId === r.id;
    if (isOpen) { activeRequest = r; activeReqView = view; }
    return rowHtml(r, isOpen) + (isOpen ? requestDetailRowHtml(r, colspan) : '');
  }).join('');
}

function renderRequestRows() {
  renderInlineQueue(requestsView, { rowHtml: requestRowHtml, colspan: 5, emptyText: 'No requests.' });
}

function issuanceRowHtml(r, isOpen) {
  return `<tr data-iss-row="${escapeHtml(r.id)}"${isOpen ? ' class="inv-row-open"' : ''}>
    <td>${requestRefHtml(r)}</td>
    <td>${statusBadge(r.status)}</td>
    <td style="text-align:center;">${(r.lines || []).length}</td>
    <td><button class="btn" data-iss-open="${escapeHtml(r.id)}">${isOpen ? '✕ Close' : 'Open'}</button></td>
  </tr>`;
}

function renderIssuanceRows() {
  renderInlineQueue(issuancesView, { rowHtml: issuanceRowHtml, colspan: 4, emptyText: 'Nothing awaiting issuance.' });
}

function toggleIssuanceRow(id) {
  if (iss.openId === id) { iss.openId = null; activeRequest = null; }
  else iss.openId = id;
  renderIssuanceRows();
}

function renderReqLine(l) {
  const need = l.item_id
    ? '<span style="color:var(--sub);">SKU assigned</span>'
    : `<span style="color:var(--err);">needs SKU</span> <select data-assign-line="${escapeHtml(l.id)}" style="max-width:180px;"><option value="">Assign…</option>${cachedItemsOptions()}</select> <button class="btn" data-assign="${escapeHtml(l.id)}">Assign</button>`;
  const label = l.item_name
    ? (l.item_sku ? `${l.item_sku} — ${l.item_name}` : l.item_name)
    : titleCase(l.category || l.free_text || 'item');
  // Per-line "issue from" picker: the request's stock may sit in different
  // warehouses/stores, so each approved line's source is chosen here at hand-over.
  const issuable = activeRequest && ['approved', 'partially_approved'].includes(activeRequest.status);
  const showLoc = issuable && l.item_id && Number(l.qty_approved) > 0 && !l.resulting_movement_id;
  const locPicker = showLoc
    ? `<div style="flex:0 0 auto;"><select data-line-location="${escapeHtml(l.id)}" style="max-width:190px;" title="Issue this line from…"><option value="">Issue from…</option>${issuingLocationOptions(l.fulfil_location_id)}</select></div>`
    : '';
  return `<div style="display:flex; gap:10px; align-items:flex-start; padding:6px 0; border-bottom:1px solid var(--border-soft);">
    <div style="flex:1 1 auto; min-width:0;">
      <strong>${escapeHtml(label)}</strong> · ${escapeHtml(l.disposition)} ·
      qty ${fmt(l.qty_requested)} · approved ${fmt(l.qty_approved)} · fulfilled ${fmt(l.qty_fulfilled)}
      ${l.item_id ? '' : '<div style="margin-top:4px;">' + need + '</div>'}
      ${(l.item_id && !Number(l.qty_fulfilled) && activeRequest && activeRequest.status === 'reserved') ? `<button class="btn" data-fulfil="${escapeHtml(l.id)}" style="margin-top:4px;">Fulfil line</button>` : ''}
    </div>
    ${locPicker}
  </div>`;
}

// Collect the per-line issuing locations chosen in the open detail: {line_id: location_id}.
function collectLineLocations(root) {
  const map = {};
  if (!root) return map;
  root.querySelectorAll('select[data-line-location]').forEach((sel) => {
    if (sel.value) map[sel.dataset.lineLocation] = sel.value;
  });
  return map;
}

let _itemsCache = null;
function cachedItemsOptions() {
  if (!_itemsCache) return '';
  return _itemsCache.map(i => `<option value="${escapeHtml(i.id)}">${escapeHtml(i.sku)} — ${escapeHtml(i.name)}</option>`).join('');
}

// Warehouse (or store/branch) options for the "issue from" picker on delivery.
function issuingLocationOptions(selectedId) {
  const eligible = (locations || []).filter(l => ['warehouse', 'store', 'branch'].includes(l.kind));
  const list = eligible.length ? eligible : (locations || []);
  return list.map(l =>
    `<option value="${escapeHtml(l.id)}"${String(l.id) === String(selectedId) ? ' selected' : ''}>${escapeHtml(l.name)}</option>`
  ).join('');
}

function requestActionsFor(r, { closeButton = true } = {}) {
  const b = [];
  // Approval is the supervisor's/root's step.
  if (r.status === 'submitted') b.push(`<button class="btn primary" data-req-action="approve">Approve all</button>`, `<button class="btn" data-req-action="reject">Reject</button>`);
  // Once approved, the storekeeper prints the note and delivers (which reserves +
  // fulfils the approved lines and completes the request).
  if (['approved', 'partially_approved', 'reserved'].includes(r.status)) {
    b.push(`<button class="btn" data-req-action="print">🖨 Print delivery note</button>`);
    // Pick the warehouse to issue from unless the request is already reserved at
    // one (producers like CRM installations raise the request without a warehouse).
    if (r.status !== 'reserved') {
      b.push(`<select data-deliver-warehouse style="max-width:220px;" title="Default source for lines without their own"><option value="">Default: issue from…</option>${issuingLocationOptions(r.fulfil_warehouse_id)}</select>`);
    }
    b.push(`<button class="btn primary" data-req-action="deliver">✅ Mark delivered</button>`);
  }
  if (r.status === 'delivered') {
    b.push(`<button class="btn" data-req-action="print">🖨 Reprint${r.delivery_note_number ? ' · ' + escapeHtml(r.delivery_note_number) : ''}</button>`);
  }
  if (['submitted', 'approved', 'partially_approved', 'reserved'].includes(r.status)) b.push(`<button class="btn" data-req-action="cancel">Cancel</button>`);
  // Undo a mistaken cancel — puts it back in the queue to be approved. Reversing a
  // REJECTION overturns an approver's decision, so only root is offered it (the API
  // enforces this too).
  if (r.status === 'cancelled') b.push(`<button class="btn" data-req-action="reopen">↺ Reopen</button>`);
  else if (r.status === 'rejected' && hasRole('root')) {
    b.push(`<button class="btn" data-req-action="reopen" title="Reverse this rejection and put the request back in the approval queue">↺ Reverse rejection</button>`);
  }
  // Always offer a way to dismiss the detail (distinct from Cancel, which
  // cancels the request itself). The inline view puts a ✕ in its header instead.
  if (closeButton) b.push(`<button class="btn" data-req-action="close">✕ Close</button>`);
  return b.join(' ');
}

function closeRequestDetail() {
  activeRequest = null;
  // Both queues are inline — collapse the open row and re-render.
  const view = activeReqView || requestsView;
  if (view.dom) view.dom.openId = null;
  if (view.render) view.render();
}

async function onRequestAction(ev) {
  const btn = ev.target.closest('button[data-req-action]');
  if (!btn || !activeRequest) return;
  const id = activeRequest.id;
  const action = btn.dataset.reqAction;
  const view = activeReqView || requestsView;
  try {
    if (action === 'close') {
      closeRequestDetail();
      return;
    }
    if (action === 'print') {
      // Capture the picked source location(s) so the note shows them and delivery
      // issues from the same place. Printing does NOT issue stock — that's Deliver.
      const selRoot = view.dom.detailActions || view.dom.tbody;
      const sel = selRoot && selRoot.querySelector('select[data-deliver-warehouse]');
      const warehouseId = sel && sel.value ? sel.value : '';
      const lineLocations = collectLineLocations(selRoot);
      const body = {};
      if (warehouseId) body.warehouse_id = warehouseId;
      if (Object.keys(lineLocations).length) body.line_locations = lineLocations;
      const updated = await api.fetch(reqUrl(id, 'delivery-note'), { method: 'POST', body: JSON.stringify(body) });
      activeRequest = updated;
      printDeliveryNote(updated);
      view.open(id);
      return;
    }
    if (action === 'approve') {
      const approvals = {};
      (activeRequest.lines || []).forEach(l => { approvals[l.id] = String(l.qty_requested); });
      await api.fetch(reqUrl(id, 'approve'), { method: 'POST', body: JSON.stringify({ approvals }) });
    } else if (action === 'reject') {
      await api.fetch(reqUrl(id, 'reject'), { method: 'POST', body: JSON.stringify({ reason: 'Rejected in console' }) });
    } else if (action === 'deliver') {
      // The pickers live in the open detail row (tbody): a request-level default
      // plus per-line overrides (stock can sit in different locations).
      const selRoot = view.dom.detailActions || view.dom.tbody;
      const sel = selRoot && selRoot.querySelector('select[data-deliver-warehouse]');
      const warehouseId = sel && sel.value ? sel.value : '';
      const lineLocations = collectLineLocations(selRoot);
      // Need a source: a default warehouse, an existing one on the request, or a
      // per-line location for the lines. The backend checks per-line coverage and
      // names any line still missing a location.
      if (!warehouseId && !activeRequest.fulfil_warehouse_id && !Object.keys(lineLocations).length) {
        flash('Pick where to issue from — a default, or per line', 'error');
        return;
      }
      const deliverBody = {};
      if (warehouseId) deliverBody.warehouse_id = warehouseId;
      if (Object.keys(lineLocations).length) deliverBody.line_locations = lineLocations;
      await api.fetch(reqUrl(id, 'deliver'), { method: 'POST', body: JSON.stringify(deliverBody) });
    } else if (action === 'cancel') {
      await api.fetch(reqUrl(id, 'cancel'), { method: 'POST', body: JSON.stringify({}) });
    } else if (action === 'reopen') {
      await api.fetch(reqUrl(id, 'reopen'), { method: 'POST', body: JSON.stringify({}) });
    }
    flash(action === 'deliver' ? 'Delivered — issued to the requester.'
      : action === 'reopen' ? 'Reopened — back in the approval queue.'
      : 'Done', 'success');
    // A delivered request leaves the issuances queue; refresh both views so each
    // reflects the new state.
    view.reload();
    loadMovesSummary();  // awaiting-issuance chip (+ value flow on deliver) may change
    if (view !== requestsView && reqDom) loadRequests();
    if (action === 'deliver') { closeRequestDetail(); } else { view.open(id); }
  } catch (err) {
    flash(err.message || 'Action failed', 'error');
  }
}

// A printable stock delivery note (reuses the window.print() pattern). Item names
// resolve from the items cache, warehouse from the locations cache.
function printDeliveryNote(r) {
  const wh = locationName(r.fulfil_warehouse_id) || '—';
  const rows = (r.lines || []).filter(l => Number(l.qty_approved) > 0).map(l => {
    const item = (_itemsCache || []).find(i => i.id === l.item_id);
    const label = item ? `${item.sku} — ${item.name}` : (l.category || l.free_text || '—');
    const src = l.fulfil_location_name || wh;  // per-line source (falls back to the request default)
    const td = 'padding:6px; border-bottom:1px solid #ddd;';
    return `<tr><td style="${td}">${escapeHtml(label)}</td><td style="${td} text-align:right;">${fmt(l.qty_approved)}</td><td style="${td}">${escapeHtml(titleCase(l.disposition || ''))}</td><td style="${td}">${escapeHtml(src)}</td></tr>`;
  }).join('');
  const date = new Date().toISOString().slice(0, 10);
  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) { flash('Allow pop-ups to print the delivery note.', 'error'); return; }
  win.document.write(`<html><head><title>Delivery Note ${escapeHtml(r.delivery_note_number || '')}</title></head>
    <body style="font-family:Arial, sans-serif; padding:24px; color:#111;">
      <div style="display:flex; justify-content:space-between; align-items:flex-start;">
        <div><div style="font-size:20px; font-weight:700;">Stock Delivery Note</div>
          <div style="color:#555; font-size:13px;">${escapeHtml(r.delivery_note_number || '')}</div></div>
        <div style="text-align:right; font-size:13px; color:#555;">Date: ${escapeHtml(date)}</div>
      </div>
      <hr style="margin:16px 0; border:none; border-top:1px solid #333;">
      <div style="font-size:13px; line-height:1.7;">
        <div><strong>For:</strong> ${escapeHtml(requestPrimary(r))} (${escapeHtml(titleCase(r.source_type || ''))})</div>
        ${requestSubtitle(r) ? `<div>${escapeHtml(requestSubtitle(r))}</div>` : ''}
        <div><strong>Request ref:</strong> ${escapeHtml(r.source_ref || '')}</div>
      </div>
      <table style="width:100%; border-collapse:collapse; margin-top:16px; font-size:13px;">
        <thead><tr style="border-bottom:2px solid #333; text-align:left;"><th style="padding:6px;">Item</th><th style="padding:6px; text-align:right;">Qty</th><th style="padding:6px;">Disposition</th><th style="padding:6px;">Issue from</th></tr></thead>
        <tbody>${rows || '<tr><td colspan="4" style="padding:6px; color:#777;">No items.</td></tr>'}</tbody>
      </table>
      <div style="margin-top:56px; display:grid; grid-template-columns:1fr 1fr; gap:40px; font-size:13px;">
        <div>Issued by (storekeeper):<br><br>________________________<br><span style="color:#777;">Name / Signature / Date</span></div>
        <div>Received by:<br><br>________________________<br><span style="color:#777;">Name / Signature / Date</span></div>
      </div>
      <script>window.print();</script>
    </body></html>`);
  win.document.close();
}

async function onLineAction(ev) {
  const assignBtn = ev.target.closest('button[data-assign]');
  const fulfilBtn = ev.target.closest('button[data-fulfil]');
  if (!activeRequest) return;
  const view = activeReqView || requestsView;
  try {
    if (assignBtn) {
      const lineId = assignBtn.dataset.assign;
      // The assign select lives in the inline row (Requests) or the detail panel
      // (Issuances); the line id is unique, so resolve it from the document.
      const sel = document.querySelector(`select[data-assign-line="${lineId}"]`);
      if (!sel || !sel.value) { flash('Pick a SKU first', 'error'); return; }
      await api.fetch(reqUrl(activeRequest.id, 'assign-sku'), { method: 'POST', body: JSON.stringify({ line_id: lineId, item_id: sel.value }) });
    } else if (fulfilBtn) {
      await api.fetch(reqUrl(activeRequest.id, 'fulfil'), { method: 'POST', body: JSON.stringify({ line_id: fulfilBtn.dataset.fulfil }) });
    } else return;
    flash('Done', 'success');
    view.open(activeRequest.id);
    view.reload();
  } catch (err) {
    flash(err.message || 'Action failed', 'error');
  }
}

function reqUrl(id, action) {
  return `/api/inventory/stock-requests/${encodeURIComponent(id)}/${action}`;
}

// Load an items cache for the assign-SKU picker (lazy, best-effort).
async function ensureItemsCache() {
  if (_itemsCache) return;
  try {
    const d = await api.fetch(API.inventoryItems(''), { method: 'GET' });
    _itemsCache = (d.items || []).map(i => ({ id: i.id, sku: i.sku, name: i.name, tracking: i.tracking, classification: i.classification }));
  } catch { _itemsCache = []; }
}

// A typed serial resolves to a specific stock unit via the scan endpoint, so
// serialized moves send stock_unit_id (which the ledger requires).
async function resolveUnitId(serial) {
  const r = await api.fetch(API.inventoryScan('code=' + encodeURIComponent(serial)), { method: 'GET' });
  if (r.kind !== 'unit' || !r.unit_id) throw new Error('No unit found for that serial');
  return r.unit_id;
}

// ===========================================================================
// LEDGER VIEW
// ===========================================================================

let ledgerDom = false;
let led = {};

// Movements view holds two tabs: the movements ledger and the stock-requests
// queue (moved here off the main sidebar to cut nav clutter).
// --- Shared pagination for the Movements-view lists ----------------------- //
// Each list endpoint returns `count` = the full total across all pages.
const INV_PAGE_SIZE = 25;

function wirePager(container, goFn) {
  if (!container || container._pagerWired) return;
  container._pagerWired = true;
  container.addEventListener('click', (ev) => {
    const b = ev.target.closest('button[data-pg]');
    if (b && !b.disabled) goFn(b.dataset.pg);
  });
}

function renderPager(container, offset, total) {
  if (!container) return;
  if (!total || total <= INV_PAGE_SIZE) { container.innerHTML = ''; return; }
  const start = offset + 1;
  const end = Math.min(offset + INV_PAGE_SIZE, total);
  container.innerHTML = `
    <span>Showing ${start}–${end} of ${total}</span>
    <span class="inv-pager-btns">
      <button class="btn compact" data-pg="prev"${offset > 0 ? '' : ' disabled'}>‹ Prev</button>
      <button class="btn compact" data-pg="next"${end < total ? '' : ' disabled'}>Next ›</button>
    </span>`;
}

// Move a list's window and reload; clamps at zero.
function pagerStep(state, dir, loadFn) {
  state.offset = dir === 'next' ? (state.offset || 0) + INV_PAGE_SIZE : Math.max(0, (state.offset || 0) - INV_PAGE_SIZE);
  loadFn();
}

function switchMovementsTab(tab) {
  const tabs = document.getElementById('invMovementsTabs');
  if (tabs) tabs.querySelectorAll('button[data-invtab]').forEach(b => b.classList.toggle('active', b.dataset.invtab === tab));
  const mp = document.getElementById('invMovementsPanel');
  const rp = document.getElementById('invRequestsPanel');
  const ip = document.getElementById('invIssuancesPanel');
  const ap = document.getElementById('invApprovalsPanel');
  if (mp) mp.hidden = tab !== 'movements';
  if (rp) rp.hidden = tab !== 'requests';
  if (ip) ip.hidden = tab !== 'issuances';
  if (ap) ap.hidden = tab !== 'discards';
  if (tab === 'requests') initRequests();
  else if (tab === 'issuances') initIssuances();
  else if (tab === 'discards') initApprovals();
  else { if (led) led.offset = 0; loadLedger(); }
}

function initLedger() {
  if (!ledgerDom) {
    ledgerDom = true;
    const g = (id) => document.getElementById(id);
    led = {
      typeFilter: g('invLedgerTypeFilter'), refreshBtn: g('invLedgerRefreshBtn'), tbody: g('invLedgerTbody'),
      pager: g('invLedgerPager'), offset: 0,
      dash: g('invMovesDash'), kpis: g('invMovesKpis'), chart: g('invMovesChart'), attn: g('invMovesAttn'),
      count: g('invMovesCount'), xrow: g('invMovesX'), valMax: g('invMovesValMax'), cntMax: g('invMovesCntMax'),
    };
    led.typeFilter && led.typeFilter.addEventListener('change', () => { led.offset = 0; loadLedger(); });
    led.refreshBtn && led.refreshBtn.addEventListener('click', () => { led.offset = 0; loadLedger(); loadMovesSummary(); });
    wirePager(led.pager, (dir) => pagerStep(led, dir, loadLedger));
    const tabs = document.getElementById('invMovementsTabs');
    tabs && tabs.addEventListener('click', (ev) => {
      const b = ev.target.closest('button[data-invtab]');
      if (b) switchMovementsTab(b.dataset.invtab);
    });
    // The attention chips jump to the relevant queue tab.
    led.attn && led.attn.addEventListener('click', (ev) => {
      const chip = ev.target.closest('button[data-attn]');
      if (chip) switchMovementsTab(chip.dataset.attn);
    });
  }
  // Land on the Movements tab each time the view is opened.
  switchMovementsTab('movements');
  loadMovesSummary();
}

function fmtMoney(v) {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
  if (Math.abs(n) >= 1e4) return (n / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function monthShort(key) {
  // key = "YYYY-MM" → "Jul" (with the year tucked into the title tooltip).
  const [y, m] = key.split('-').map(Number);
  return new Date(y, (m || 1) - 1, 1).toLocaleString(undefined, { month: 'short' });
}

async function loadMovesSummary() {
  if (!led.dash) return;
  try {
    const d = await api.fetch(API.inventoryMovementsSummary(), { method: 'GET' });
    renderMovesDash(d);
    led.dash.hidden = false;
  } catch {
    led.dash.hidden = true;  // keep the tabs usable even if the dashboard fails
  }
}

function renderMovesDash(d) {
  const delta = (d.count_this_month || 0) - (d.count_prev_month || 0);
  const arrow = delta > 0 ? '▲' : (delta < 0 ? '▼' : '·');
  const shrinkPct = d.shrinkage_pct ? ` · ${(d.shrinkage_pct * 100).toFixed(1)}%` : '';
  led.kpis.innerHTML = `
    <div class="inv-card"><div class="label">Received (12m)</div><div class="value">${fmtMoney(d.value_in)}</div><div class="sub">Stock value in</div></div>
    <div class="inv-card"><div class="label">Consumed/deployed (12m)</div><div class="value">${fmtMoney(d.value_out)}</div><div class="sub">${Number(d.out_count || 0)} issued/consumed/deployed · net ${fmtMoney(d.net_value)}</div></div>
    <div class="inv-card"><div class="label">Deployed to field (12m)</div><div class="value">${fmtMoney(d.deployed_value)}</div><div class="sub">${Number(d.deployed_count || 0)} deployed into infrastructure</div></div>
    <div class="inv-card kpi-out"><div class="label">Shrinkage (12m)</div><div class="value${Number(d.shrinkage_value) ? '' : ' is-zero'}">${fmtMoney(d.shrinkage_value)}</div><div class="sub">${d.shrinkage_count || 0} write-offs/disposals${shrinkPct}</div></div>
    <div class="inv-card"><div class="label">Movements (12m)</div><div class="value">${Number(d.movement_count || 0).toLocaleString()}</div><div class="sub">${arrow} ${d.count_this_month || 0} this month vs ${d.count_prev_month || 0} last</div></div>`;

  const series = d.series || [];
  // Value and movement-count are different measures on different scales. Rather than a
  // misleading dual y-axis, they render as two aligned panels sharing one month x-axis.
  const maxVal = Math.max(1, ...series.map(m => Math.max(Number(m.inbound), Number(m.outbound), Number(m.shrinkage))));
  const maxCount = Math.max(1, ...series.map(m => Number(m.count) || 0));
  const hv = (v) => `${Math.max(Number(v) > 0 ? 2 : 0, Math.round(Number(v) / maxVal * 100))}%`;
  const hc = (v) => `${Math.max(Number(v) > 0 ? 2 : 0, Math.round(Number(v) / maxCount * 100))}%`;
  const tip = (m) => `${m.month} · in ${fmtMoney(m.inbound)} · out ${fmtMoney(m.outbound)} · shrink ${fmtMoney(m.shrinkage)} · ${m.count} moves`;
  led.chart.innerHTML = series.map(m => `<div class="inv-chart-col" title="${escapeHtml(tip(m))}">
      <div class="inv-chart-bars">
        <span class="bar in" style="height:${hv(m.inbound)}"></span>
        <span class="bar out" style="height:${hv(m.outbound)}"></span>
        ${Number(m.shrinkage) > 0 ? `<span class="bar shrink" style="height:${hv(m.shrinkage)}"></span>` : ''}
      </div>
    </div>`).join('');
  if (led.count) led.count.innerHTML = series.map(m => `<div class="inv-chart-col" title="${escapeHtml(tip(m))}">
      <div class="inv-chart-bars">
        ${Number(m.count) > 0 ? `<span class="bar count" style="height:${hc(m.count)}"></span>` : ''}
      </div>
    </div>`).join('');
  if (led.xrow) led.xrow.innerHTML = series.map(m => `<div class="inv-chart-col"><div class="inv-chart-x">${escapeHtml(monthShort(m.month))}</div></div>`).join('');
  if (led.valMax) led.valMax.textContent = `peak ${fmtMoney(maxVal)}`;
  if (led.cntMax) led.cntMax.textContent = `peak ${Number(maxCount).toLocaleString()}`;

  const chip = (tab, label, n) => `<button class="inv-chip${n > 0 ? ' attn' : ''}" data-attn="${tab}">${label} <b>${n}</b></button>`;
  led.attn.innerHTML = chip('discards', 'Pending approvals', d.pending_approvals || 0)
    + chip('issuances', 'Awaiting issuance', d.awaiting_issuance || 0);
}

async function loadLedger() {
  if (!led.tbody) return;
  led.tbody.innerHTML = `<tr><td colspan="7" style="color:var(--sub);">Loading…</td></tr>`;
  const p = new URLSearchParams();
  if (led.typeFilter && led.typeFilter.value) p.set('movement_type', led.typeFilter.value);
  p.set('limit', String(INV_PAGE_SIZE));
  p.set('offset', String(led.offset || 0));
  try {
    const data = await api.fetch(API.inventoryMovements(p.toString()), { method: 'GET' });
    const items = data.items || [];
    renderPager(led.pager, led.offset || 0, data.count || 0);
    led.tbody.innerHTML = items.length ? items.map(m => {
      const itemLabel = m.item_name ? `${m.item_sku ? m.item_sku + ' · ' : ''}${m.item_name}` : '—';
      const from = m.from_location_name, to = m.to_location_name;
      const fromTo = (from || to) ? `${from || '—'} → ${to || '—'}` : '—';
      const src = m.source_label || `${titleCase(m.source_type || '')}${m.source_ref ? ' ' + m.source_ref : ''}`.trim();
      return `<tr>
      <td>${escapeHtml((m.occurred_at || '').replace('T', ' ').slice(0, 16))}</td>
      <td>${movementBadge(m.movement_type)}</td>
      <td>${escapeHtml(itemLabel)}</td>
      <td style="text-align:right;">${fmt(m.quantity)}</td>
      <td style="text-align:right;">${m.total_value != null ? fmt(m.total_value) : ''}</td>
      <td style="font-size:12px; color:var(--sub);">${escapeHtml(fromTo)}</td>
      <td style="font-size:12px;">${escapeHtml(src || '—')}</td>
    </tr>`;
    }).join('') : `<tr><td colspan="7" style="color:var(--sub);">No movements.</td></tr>`;
  } catch (err) {
    if (led.pager) led.pager.innerHTML = '';
    led.tbody.innerHTML = `<tr><td colspan="7" style="color:var(--err);">${escapeHtml(err.message || 'Failed to load')}</td></tr>`;
  }
}

// ===========================================================================
// APPROVALS QUEUE (tab under Movements) — two-person control for adjust /
// write-off / dispose. Approving here posts the movement server-side.
// ===========================================================================

let apprDom = false;
let apr = {};
const SUBJECT_LABEL = { adjustment: 'Adjust', write_off: 'Write-off', disposal: 'Dispose' };

function initApprovals() {
  if (!apprDom) {
    apprDom = true;
    const g = (id) => document.getElementById(id);
    apr = { statusFilter: g('invApprStatusFilter'), refreshBtn: g('invApprRefreshBtn'), tbody: g('invApprTbody'), pager: g('invApprPager'), offset: 0, openId: null, items: [] };
    apr.statusFilter && apr.statusFilter.addEventListener('change', () => { apr.offset = 0; loadApprovals(); });
    apr.refreshBtn && apr.refreshBtn.addEventListener('click', () => { apr.offset = 0; loadApprovals(); });
    wirePager(apr.pager, (dir) => pagerStep(apr, dir, loadApprovals));
    // Inline detail (like Requests / Issuances): open/close + approve/reject are
    // all delegated off the table body.
    apr.tbody && apr.tbody.addEventListener('click', (ev) => {
      const openBtn = ev.target.closest('button[data-appr-open]');
      if (openBtn) { toggleApprovalRow(openBtn.dataset.apprOpen); return; }
      onApprovalAction(ev);
    });
  }
  apr.offset = 0;
  loadApprovals();
}

function approvalRowHtml(a, isOpen) {
  const item = a.item_name ? `${a.item_sku ? a.item_sku + ' · ' : ''}${a.item_name}` : '—';
  const subjBadge = badge(SUBJECT_LABEL[a.subject] || titleCase(a.subject), a.subject === 'disposal' ? 'off' : 'warn');
  const statBadge = badge(titleCase(a.status), a.status === 'approved' ? 'ok' : (a.status === 'rejected' ? 'warn' : 'off'));
  return `<tr data-appr-row="${escapeHtml(a.id)}"${isOpen ? ' class="inv-row-open"' : ''}>
    <td>${escapeHtml((a.created_at || '').replace('T', ' ').slice(0, 16))}</td>
    <td>${subjBadge}</td>
    <td>${escapeHtml(item)}</td>
    <td>${statBadge}</td>
    <td><button class="btn" data-appr-open="${escapeHtml(a.id)}">${isOpen ? '✕ Close' : 'Open'}</button></td>
  </tr>`;
}

function approvalDetailRowHtml(a) {
  const item = a.item_name ? `${a.item_sku ? a.item_sku + ' · ' : ''}${a.item_name}` : '—';
  const kv = (k, v) => `<div style="padding:4px 0; border-bottom:1px solid var(--border-soft);"><span style="color:var(--sub);">${k}:</span> ${v}</div>`;
  const body = kv('Item', escapeHtml(item))
    + kv('Location', escapeHtml(a.location_name || '—'))
    + kv('Quantity', fmt(a.quantity))
    + kv('Reason', escapeHtml(a.reason || '—'))
    + kv('Requested by', escapeHtml(a.requested_by_name || '—'))
    + (a.decision_note ? kv('Decision note', escapeHtml(a.decision_note)) : '');
  const actions = a.status === 'pending'
    ? `<button class="btn primary" data-appr-approve="${escapeHtml(a.id)}">Approve</button> <button class="btn ghost" data-appr-reject="${escapeHtml(a.id)}">Reject</button>`
    : '';
  return `<tr class="inv-inline-detail"><td colspan="5"><div class="inv-inline-box">
      <div class="inv-inline-head">
        <div><strong>${escapeHtml(SUBJECT_LABEL[a.subject] || titleCase(a.subject))}</strong></div>
        <button class="btn ghost" data-appr-open="${escapeHtml(a.id)}" title="Close">✕</button>
      </div>
      <div style="font-size:13px;">${body}</div>
      ${actions ? `<div class="inv-inline-actions">${actions}</div>` : ''}
    </div></td></tr>`;
}

function renderApprovalRows() {
  if (!apr.tbody) return;
  const items = apr.items || [];
  if (!items.length) { apr.tbody.innerHTML = `<tr><td colspan="5" style="color:var(--sub);">No approvals.</td></tr>`; return; }
  if (apr.openId && !items.some(a => a.id === apr.openId)) apr.openId = null;
  apr.tbody.innerHTML = items.map(a => {
    const isOpen = apr.openId === a.id;
    return approvalRowHtml(a, isOpen) + (isOpen ? approvalDetailRowHtml(a) : '');
  }).join('');
}

function toggleApprovalRow(id) {
  apr.openId = apr.openId === id ? null : id;
  renderApprovalRows();
}

async function loadApprovals() {
  if (!apr.tbody) return;
  apr.tbody.innerHTML = `<tr><td colspan="5" style="color:var(--sub);">Loading…</td></tr>`;
  const p = new URLSearchParams();
  if (apr.statusFilter && apr.statusFilter.value) p.set('status', apr.statusFilter.value);
  p.set('limit', String(INV_PAGE_SIZE));
  p.set('offset', String(apr.offset || 0));
  try {
    const data = await api.fetch(API.inventoryApprovalsList(p.toString()), { method: 'GET' });
    apr.items = data.items || [];
    renderPager(apr.pager, apr.offset || 0, data.count || 0);
    renderApprovalRows();
  } catch (err) {
    if (apr.pager) apr.pager.innerHTML = '';
    apr.tbody.innerHTML = `<tr><td colspan="5" style="color:var(--err);">${escapeHtml(err.message || 'Failed to load')}</td></tr>`;
  }
}

async function onApprovalAction(ev) {
  const approveBtn = ev.target.closest('button[data-appr-approve]');
  const rejectBtn = ev.target.closest('button[data-appr-reject]');
  if (!approveBtn && !rejectBtn) return;
  const approved = !!approveBtn;
  const id = approved ? approveBtn.dataset.apprApprove : rejectBtn.dataset.apprReject;
  const body = { approved };
  if (!approved) {
    const note = window.prompt('Reason for rejecting? (optional)');
    if (note) body.decision_note = note;
  }
  try {
    await api.fetch(API.inventoryApprovalDecide(id), { method: 'POST', body: JSON.stringify(body) });
    flash(approved ? 'Approved — movement posted.' : 'Rejected.', 'success');
    loadApprovals();
    loadMovesSummary();          // pending-approvals chip + shrinkage may change
    if (stockDom) loadStock();  // reflect the posted movement in balances
  } catch (err) {
    // The most common denial: you can't approve your own request.
    flash(err.message || 'Action failed', 'error');
  }
}

// ===========================================================================
// ASSETS VIEW
// ===========================================================================

let assetsDom = false;
let ast = {};
let activeAsset = null;
let _assetsOverdue = false; // KPI "Maintenance overdue" lens (not a `state` filter)
// Client-side paging/sort/search/grouping over the full asset set the endpoint
// returns (server handles only state/fixed_only/overdue filters).
const ASSETS_PAGE_SIZE = 30;
let _assetsAll = [];
let _assetsPage = 0;
let _assetsSort = 'name';       // name (default) | serial | recent (server order)
let _assetsSearch = '';         // matches serial / sku / name / location
let _assetsGroupLoc = false;    // group rows under a per-location subheader
let _assetsSearchTimer = null;

// The per-unit actions available on a serialized unit, by its item classification
// and current state. All post against this exact unit (serial pre-filled).
function unitActionsFor(unit) {
  const acts = [];
  const c = unit.classification;
  const st = unit.state;
  // Infrastructure goes out to a facility through a work order or Operations,
  // so the only site action here is bringing a deployed unit back in.
  if (c === 'infrastructure' && st === 'deployed') acts.push({ action: 'deinstall', label: 'De-install' });
  if (c === 'tool') {
    if (st === 'assigned') acts.push({ action: 'return', label: 'Return' });
    else acts.push({ action: 'assign', label: 'Assign' });
  }
  // Any in-hand unit can be moved between locations or retired.
  if (st !== 'retired') {
    acts.push({ action: 'transfer', label: 'Transfer' });
    acts.push({ action: 'write_off', label: 'Write-off' });
    acts.push({ action: 'dispose', label: 'Dispose' });
  }
  return acts;
}

async function openAssetDetail(unitId) {
  try {
    const d = await api.fetch(API.inventoryAsset(unitId), { method: 'GET' });
    const u = d.unit;
    activeAsset = u;
    ast.detailEmpty.hidden = true;
    ast.detailBody.hidden = false;
    ast.detailName.textContent = `${u.serial}${u.item_name ? ' · ' + u.item_name : ''}`;
    ast.detailMeta.innerHTML = `${u.classification ? classBadge(u.classification) + ' · ' : ''}${badge(titleCase(u.state || ''), '')}${u.is_fixed_asset ? ' · ' + badge('Fixed', 'violet') : ''}`;
    const kv = [
      ['Item', u.item_sku ? `${escapeHtml(u.item_sku)} · ${escapeHtml(u.item_name || '')}` : escapeHtml(u.item_name || '—')],
      ['Location', escapeHtml(u.location_name || '—')],
      ['Facility', escapeHtml(u.facility_ref || '—')],
      ['Ownership', escapeHtml(titleCase(u.ownership_state || ''))],
      ['Condition', escapeHtml(u.condition_note || '—')],
      ['Unit cost', u.unit_cost != null ? fmt(u.unit_cost) : '—'],
    ];
    // PM schedule (infrastructure/tool assets — set from Operations → Maintenance).
    if (u.classification === 'infrastructure' || u.classification === 'tool') {
      const anchor = u.commissioned_at ? escapeHtml(u.commissioned_at.slice(0, 10)) : '—';
      const sched = u.maintenance_interval
        ? `${escapeHtml(u.maintenance_interval)}${u.maintenance_work_set_name ? ' · ' + escapeHtml(u.maintenance_work_set_name) : ''}`
        : '<span style="color:var(--sub);">inherits facility / none</span>';
      kv.push(['PM schedule', sched]);
      kv.push(['Commission date', anchor]);
    }
    ast.detailKv.innerHTML = kv.map(([k, v]) => `<div><span class="k">${escapeHtml(k)}</span><span class="v">${v}</span></div>`).join('');
    const moves = d.movements || [];
    ast.detailHistory.innerHTML = moves.length
      ? moves.map(m => {
          const ref = movementRef(m);
          return `<div style="margin-bottom:3px;">${escapeHtml((m.occurred_at || '').slice(0, 10))} · ${movementBadge(m.movement_type)}${ref ? ` · <span style="color:var(--sub);">${escapeHtml(ref)}</span>` : ''}</div>`;
        }).join('')
      : '<span style="color:var(--sub);">No movements yet.</span>';
    ast.detailActions.innerHTML = unitActionsFor(u)
      .map(a => `<button class="btn" data-asset-action="${a.action}">${escapeHtml(a.label)}</button>`).join(' ')
      + ` <button class="btn ghost" data-asset-action="edit_item" title="Edit this asset's item details (name, SKU, category, classification)">✎ Edit item</button>`;
  } catch (err) {
    flash(err.message || 'Failed to load asset', 'error');
  }
}

// Edit the underlying item of an asset unit — fetch the full item (the unit only
// carries a subset), then reuse the item edit dialog. A registered asset always
// has a unit, so tracking is locked (fixed assets stay serialized regardless).
async function openEditAssetItem(unit) {
  if (!unit || !unit.item_id) return;
  try {
    const d = await api.fetch(API.inventoryItem(unit.item_id), { method: 'GET' });
    if (d && d.item) openEditItem(d.item, { hasStock: true });
  } catch (err) {
    flash(err.message || 'Failed to load item', 'error');
  }
}

// Act on this exact unit: open the movement dialog with its item + serial locked in.
function runAssetAction(unit, action) {
  const item = { id: unit.item_id, sku: unit.item_sku, name: unit.item_name, classification: unit.classification, tracking: 'serialized' };
  openMove(item, action, { serial: unit.serial, fromLocationId: unit.location_id, unitId: unit.id });
}

function initAssets() {
  if (!assetsDom) {
    assetsDom = true;
    const g = (id) => document.getElementById(id);
    ast = {
      stateFilter: g('invAssetsStateFilter'), fixedFilter: g('invAssetsFixedFilter'), refreshBtn: g('invAssetsRefreshBtn'),
      search: g('invAssetsSearch'), sort: g('invAssetsSort'), groupLoc: g('invAssetsGroupLoc'),
      prev: g('invAssetsPrev'), next: g('invAssetsNext'), pageInfo: g('invAssetsPageInfo'),
      registerBtn: g('invAssetRegisterBtn'), newFixedBtn: g('invAssetNewFixedBtn'), tbody: g('invAssetsTbody'),
      detailEmpty: g('invAssetDetailEmpty'), detailBody: g('invAssetDetailBody'), detailName: g('invAssetDetailName'),
      detailMeta: g('invAssetDetailMeta'), detailActions: g('invAssetDetailActions'), detailKv: g('invAssetDetailKv'),
      detailHistory: g('invAssetDetailHistory'),
      kpis: g('invAssetsKpis'),
      kpiTotal: g('invAssetKpiTotal'), kpiValue: g('invAssetKpiValue'), kpiDeployed: g('invAssetKpiDeployed'),
      kpiStock: g('invAssetKpiStock'), kpiOverdue: g('invAssetKpiOverdue'), kpiFaulty: g('invAssetKpiFaulty'),
      kpiRetired: g('invAssetKpiRetired'), kpiRetiredValue: g('invAssetKpiRetiredValue'),
    };
    // Manual filter changes clear the overdue lens (it isn't a `state`).
    ast.stateFilter && ast.stateFilter.addEventListener('change', () => { _assetsOverdue = false; loadAssets(); });
    ast.fixedFilter && ast.fixedFilter.addEventListener('change', () => { _assetsOverdue = false; loadAssets(); });
    ast.refreshBtn && ast.refreshBtn.addEventListener('click', () => loadAssets());
    // Search / sort / grouping / paging are client-side over the loaded set — no refetch.
    ast.search && ast.search.addEventListener('input', () => {
      clearTimeout(_assetsSearchTimer);
      _assetsSearchTimer = setTimeout(() => {
        _assetsSearch = ast.search.value || '';
        _assetsPage = 0;
        paintAssets();
      }, 150);
    });
    ast.sort && ast.sort.addEventListener('change', () => {
      _assetsSort = ast.sort.value || 'name';
      _assetsPage = 0;
      paintAssets();
    });
    ast.groupLoc && ast.groupLoc.addEventListener('change', () => {
      _assetsGroupLoc = !!ast.groupLoc.checked;
      if (ast.sort) ast.sort.disabled = _assetsGroupLoc; // grouping forces location→name order
      _assetsPage = 0;
      paintAssets();
    });
    ast.prev && ast.prev.addEventListener('click', () => { _assetsPage -= 1; paintAssets(); });
    ast.next && ast.next.addEventListener('click', () => { _assetsPage += 1; paintAssets(); });
    // Click a KPI card → filter the register to those assets (all cards imply fixed-only).
    ast.kpis && ast.kpis.addEventListener('click', (ev) => {
      const card = ev.target.closest('[data-asset-filter]');
      if (!card) return;
      const f = card.dataset.assetFilter;
      if (ast.fixedFilter) ast.fixedFilter.checked = true;
      _assetsOverdue = (f === 'overdue');
      const asState = (f === 'all' || f === 'overdue') ? '' : f;
      if (ast.stateFilter) ast.stateFilter.value = asState;
      loadAssets();
    });
    ast.registerBtn && ast.registerBtn.addEventListener('click', openRegisterAsset);
    ast.newFixedBtn && ast.newFixedBtn.addEventListener('click', openNewFixedAsset);
    ast.tbody && ast.tbody.addEventListener('click', (ev) => {
      const row = ev.target.closest('tr[data-asset-row]');
      if (row) openAssetDetail(row.dataset.assetRow);
    });
    ast.detailActions && ast.detailActions.addEventListener('click', (ev) => {
      const btn = ev.target.closest('button[data-asset-action]');
      if (!btn || !activeAsset) return;
      if (btn.dataset.assetAction === 'edit_item') return void openEditAssetItem(activeAsset);
      runAssetAction(activeAsset, btn.dataset.assetAction);
    });
    // The register-asset + movement dialogs reuse the stock view's forms + handlers.
    resolveStockDom();
    wireStock();
    ensureItemsCache();
  }
  loadAssets();
}

async function loadAssetSummary() {
  if (!ast.kpiTotal) return;
  try {
    const k = await api.fetch(API.inventoryAssetsSummary, { method: 'GET' });
    setKpi(ast.kpiTotal, k.total);
    setKpi(ast.kpiDeployed, k.deployed);
    setKpi(ast.kpiStock, k.in_stock);
    setKpi(ast.kpiOverdue, k.maintenance_overdue);
    setKpi(ast.kpiFaulty, k.faulty_quarantined);
    setKpi(ast.kpiRetired, k.retired_period);
    if (ast.kpiValue) ast.kpiValue.textContent = `book value ${fmtMoney(k.book_value)}`;
    if (ast.kpiRetiredValue) ast.kpiRetiredValue.textContent = `written off ${fmtMoney(k.retired_period_value)}`;
  } catch { /* leave the cards at their last values on a transient error */ }
}

async function loadAssets() {
  if (!ast.tbody) return;
  loadAssetSummary();
  ast.tbody.innerHTML = `<tr><td colspan="6" style="color:var(--sub);">Loading…</td></tr>`;
  const p = new URLSearchParams();
  if (ast.stateFilter && ast.stateFilter.value) p.set('state', ast.stateFilter.value);
  if (ast.fixedFilter && ast.fixedFilter.checked) p.set('fixed_only', 'true');
  if (_assetsOverdue) p.set('overdue', 'true');
  try {
    const data = await api.fetch(API.inventoryAssets(p.toString()), { method: 'GET' });
    _assetsAll = data.items || [];
    _assetsPage = 0;
    paintAssets();
  } catch (err) {
    _assetsAll = [];
    ast.tbody.innerHTML = `<tr><td colspan="6" style="color:var(--err);">${escapeHtml(err.message || 'Failed to load')}</td></tr>`;
    renderAssetsPager(0, 0, 0);
  }
}

function assetRowHtml(u) {
  const itemLabel = u.item_name ? `${u.item_sku ? u.item_sku + ' · ' : ''}${u.item_name}` : '—';
  const fixedTag = u.is_fixed_asset ? ' ' + badge('Fixed', 'violet') : '';
  return `<tr class="inv-clickable-row" data-asset-row="${escapeHtml(u.id)}">
      <td><strong>${escapeHtml(u.serial)}</strong>${fixedTag}</td>
      <td>${escapeHtml(itemLabel)}${u.category_name ? ` <span style="font-size:11px; color:var(--sub);">(${escapeHtml(u.category_name)})</span>` : ''}</td>
      <td>${badge(titleCase(u.state || ''), '')}</td>
      <td style="color:var(--sub);">${escapeHtml(u.location_name || '—')}</td>
      <td>${escapeHtml(u.facility_ref || '—')}</td>
      <td>${escapeHtml(titleCase(u.ownership_state || ''))}</td>
    </tr>`;
}

function assetGroupHeaderHtml(loc) {
  return `<tr class="inv-asset-group"><td colspan="6" style="background:var(--panel-2,#f1f5f9); font-weight:700; font-size:12px; padding:6px 8px;">📍 ${escapeHtml(loc)}</td></tr>`;
}

// Filter (search) + sort the full set. When grouping is on, the primary sort key
// is location, then name, so same-location rows are contiguous for subheaders.
function sortedFilteredAssets() {
  const q = _assetsSearch.trim().toLowerCase();
  let arr = _assetsAll;
  if (q) {
    arr = arr.filter(u => [u.serial, u.item_sku, u.item_name, u.location_name]
      .some(f => (f || '').toLowerCase().includes(q)));
  } else {
    arr = arr.slice();
  }
  const byName = (a, b) =>
    (a.item_name || '').localeCompare(b.item_name || '', undefined, { sensitivity: 'base' }) ||
    (a.serial || '').localeCompare(b.serial || '', undefined, { sensitivity: 'base' });
  if (_assetsGroupLoc) {
    arr.sort((a, b) =>
      (a.location_name || '￿').localeCompare(b.location_name || '￿', undefined, { sensitivity: 'base' })
      || byName(a, b));
  } else if (_assetsSort === 'serial') {
    arr.sort((a, b) => (a.serial || '').localeCompare(b.serial || '', undefined, { sensitivity: 'base', numeric: true }));
  } else if (_assetsSort === 'recent') {
    /* keep the server order (updated_at desc) */
  } else {
    arr.sort(byName);
  }
  return arr;
}

function paintAssets() {
  if (!ast.tbody) return;
  const rows = sortedFilteredAssets();
  const pageCount = Math.max(1, Math.ceil(rows.length / ASSETS_PAGE_SIZE));
  if (_assetsPage > pageCount - 1) _assetsPage = pageCount - 1;
  if (_assetsPage < 0) _assetsPage = 0;
  if (!rows.length) {
    ast.tbody.innerHTML = `<tr><td colspan="6" style="color:var(--sub);">No assets${_assetsSearch ? ' match your search' : ''}.</td></tr>`;
    renderAssetsPager(0, 0, 0);
    return;
  }
  const start = _assetsPage * ASSETS_PAGE_SIZE;
  const pageRows = rows.slice(start, start + ASSETS_PAGE_SIZE);
  let html = '';
  if (_assetsGroupLoc) {
    let curLoc = null;
    for (const u of pageRows) {
      const loc = u.location_name || 'No location';
      if (loc !== curLoc) { html += assetGroupHeaderHtml(loc); curLoc = loc; }
      html += assetRowHtml(u);
    }
  } else {
    html = pageRows.map(assetRowHtml).join('');
  }
  ast.tbody.innerHTML = html;
  renderAssetsPager(start, pageRows.length, rows.length);
}

function renderAssetsPager(start, shown, total) {
  if (ast.pageInfo) ast.pageInfo.textContent = total ? `${start + 1}–${start + shown} of ${total}` : '0 assets';
  const pageCount = Math.max(1, Math.ceil(total / ASSETS_PAGE_SIZE));
  if (ast.prev) ast.prev.disabled = _assetsPage <= 0;
  if (ast.next) ast.next.disabled = _assetsPage >= pageCount - 1;
}

// ===========================================================================
// PURCHASING VIEW
// ===========================================================================

let poDom = false;
let po = {};
let suppliers = [];
let allLocations = [];
let allCategories = [];
let editingSupplierId = null;
let editingLocationId = null;
let editingCategoryId = null;
let activePo = null;
let _pos = [];

// Suggest the next PO number: PO-{year}-{seq}, seq one above the highest seen for
// the current year. Editable; the server validates uniqueness on submit.
function suggestPoNumber() {
  const year = new Date().getFullYear();
  const prefix = `PO-${year}-`;
  let max = 0;
  for (const p of _pos) {
    const n = String(p.po_number || '');
    if (n.startsWith(prefix)) {
      const seq = parseInt(n.slice(prefix.length), 10);
      if (!Number.isNaN(seq) && seq > max) max = seq;
    }
  }
  return `${prefix}${String(max + 1).padStart(4, '0')}`;
}

function switchPurchasingTab(tab) {
  const tabs = document.getElementById('invPurchasingTabs');
  if (tabs) tabs.querySelectorAll('button[data-potab]').forEach(b => b.classList.toggle('active', b.dataset.potab === tab));
  const pp = document.getElementById('invPoPanel');
  const sp = document.getElementById('invSettingsPanel');
  if (pp) pp.hidden = tab !== 'pos';
  if (sp) sp.hidden = tab !== 'settings';
  if (tab === 'settings') { loadCategoriesTable(); loadSuppliers(); loadLocations(); }
  else loadPos();
}

function initPurchasing() {
  if (!poDom) {
    poDom = true;
    const g = (id) => document.getElementById(id);
    po = {
      refreshBtn: g('invPoRefreshBtn'), categoryNewBtn: g('invCategoryNewBtn'), supplierNewBtn: g('invSupplierNewBtn'), locationNewBtn: g('invLocationNewBtn'), poNewBtn: g('invPoNewBtn'),
      tbody: g('invPoTbody'), detail: g('invPoDetail'), detailEmpty: g('invPoDetailEmpty'), detailBody: g('invPoDetailBody'),
      detailName: g('invPoDetailName'), detailMeta: g('invPoDetailMeta'), detailActions: g('invPoDetailActions'), detailLines: g('invPoDetailLines'),
      categoryTbody: g('invCategoryTbody'), supplierTbody: g('invSupplierTbody'), locationTbody: g('invLocationTbody'),
      supplierDlg: g('invSupplierDlg'), supplierForm: g('invSupplierForm'), supplierDlgTitle: g('invSupplierDlgTitle'), supplierSubmit: g('invSupplierSubmit'),
      supplierCode: g('invSupplierCode'), supplierName: g('invSupplierName'),
      supplierContact: g('invSupplierContact'), supplierEmail: g('invSupplierEmail'), supplierPhone: g('invSupplierPhone'),
      supplierActiveField: g('invSupplierActiveField'), supplierActive: g('invSupplierActive'),
      locationDlg: g('invLocationDlg'), locationForm: g('invLocationForm'), locationDlgTitle: g('invLocationDlgTitle'), locationSubmit: g('invLocationSubmit'),
      locationCode: g('invLocationCode'), locationName: g('invLocationName'), locationKind: g('invLocationKind'), locationAddress: g('invLocationAddress'),
      locationActiveField: g('invLocationActiveField'), locationActive: g('invLocationActive'),
      poDlg: g('invPoDlg'), poForm: g('invPoForm'), poNumber: g('invPoNumber'), poSupplier: g('invPoSupplier'),
      poExpected: g('invPoExpected'), poLocation: g('invPoLocation'), poLines: g('invPoLines'), poAddLine: g('invPoAddLine'),
    };
    po.refreshBtn && po.refreshBtn.addEventListener('click', () => loadPos());
    const ptabs = document.getElementById('invPurchasingTabs');
    ptabs && ptabs.addEventListener('click', (ev) => {
      const b = ev.target.closest('button[data-potab]');
      if (b) switchPurchasingTab(b.dataset.potab);
    });
    po.categoryNewBtn && po.categoryNewBtn.addEventListener('click', () => openCategory());
    const importBtn = document.getElementById('invImportBtn');
    importBtn && importBtn.addEventListener('click', openImport);
    po.supplierNewBtn && po.supplierNewBtn.addEventListener('click', () => openSupplier());
    po.locationNewBtn && po.locationNewBtn.addEventListener('click', () => openLocation());
    po.poNewBtn && po.poNewBtn.addEventListener('click', openNewPo);
    po.supplierForm && po.supplierForm.addEventListener('submit', onSaveSupplier);
    po.locationForm && po.locationForm.addEventListener('submit', onSaveLocation);
    po.poForm && po.poForm.addEventListener('submit', onCreatePo);
    po.categoryTbody && po.categoryTbody.addEventListener('click', (ev) => {
      const b = ev.target.closest('button[data-category-edit]');
      if (b) openCategory(allCategories.find(x => x.id === b.dataset.categoryEdit));
    });
    po.poAddLine && po.poAddLine.addEventListener('click', () => addPoLineRow());
    po.tbody && po.tbody.addEventListener('click', (ev) => {
      const b = ev.target.closest('button[data-po-open]');
      if (b) openPoDetail(b.dataset.poOpen);
    });
    po.supplierTbody && po.supplierTbody.addEventListener('click', (ev) => {
      const b = ev.target.closest('button[data-supplier-edit]');
      if (b) openSupplier(suppliers.find(x => x.id === b.dataset.supplierEdit));
    });
    po.locationTbody && po.locationTbody.addEventListener('click', (ev) => {
      const b = ev.target.closest('button[data-location-edit]');
      if (b) openLocation(allLocations.find(x => x.id === b.dataset.locationEdit));
    });
    po.detailActions && po.detailActions.addEventListener('click', onPoAction);
    po.detailLines && po.detailLines.addEventListener('click', onPoLineReceive);
    // Auto-generate codes from names (editable; stops once the user edits a code).
    wireAutoCode(po.supplierName, po.supplierCode, { upper: true, maxLen: 32 });
    wireAutoCode(po.locationName, po.locationCode, { upper: true, maxLen: 32 });
  }
  ensureLookups().catch(() => {});
  ensureItemsCache();
  // Land on the Purchase orders tab; Settings tables load when that tab opens.
  switchPurchasingTab('pos');
}

// Derive a code from a name: alphanumeric runs → underscores, trimmed, cased and
// capped to match the backend's normalization (category=lower_snake ≤64;
// supplier/location=UPPER ≤32).
function slugCode(name, { upper = false, maxLen = 64 } = {}) {
  let s = String(name || '').trim().replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  s = upper ? s.toUpperCase() : s.toLowerCase();
  return s.slice(0, maxLen).replace(/_+$/g, '');
}

// Keep a code field auto-filled from its name field until the user edits the code
// by hand (then we stop overwriting their choice). Reset via resetAutoCode on open.
function wireAutoCode(nameEl, codeEl, opts = {}) {
  if (!nameEl || !codeEl) return;
  const maxLen = opts.maxLen || 64;
  nameEl.addEventListener('input', () => {
    if (codeEl.readOnly || codeEl.dataset.userEdited === '1') return;
    const base = slugCode(nameEl.value, opts);
    if (!base) { codeEl.value = ''; return; }
    const digit = codeEl.dataset.rand || '';
    // Leave room for the "_N" suffix within the field's max length.
    codeEl.value = digit ? `${base.slice(0, maxLen - 2)}_${digit}` : base;
  });
  codeEl.addEventListener('input', () => { codeEl.dataset.userEdited = '1'; });
}

// The code to submit: whatever's in the field, else derived from the name (with a
// random digit, matching the live auto-fill). Guarantees a code even if the live
// fill didn't run for some reason.
function autoCodeValue(codeEl, nameEl, opts = {}) {
  const typed = (codeEl && codeEl.value.trim()) || '';
  if (typed) return typed;
  const base = slugCode(nameEl ? nameEl.value : '', opts);
  if (!base) return '';
  const maxLen = opts.maxLen || 64;
  const digit = String(Math.floor(Math.random() * 10));
  return `${base.slice(0, maxLen - 2)}_${digit}`;
}

function resetAutoCode(codeEl) {
  if (!codeEl) return;
  codeEl.dataset.userEdited = '';
  // A stable random digit (0–9) appended to the slug lowers the collision rate
  // when two names slugify the same; picked fresh each time the dialog opens and
  // held steady while the user types the name.
  codeEl.dataset.rand = String(Math.floor(Math.random() * 10));
}

// --- categories management ---

async function loadCategoriesTable() {
  if (!po.categoryTbody) return;
  try {
    const d = await api.fetch(API.inventoryCategories(), { method: 'GET' });  // all, incl. inactive
    allCategories = d.items || [];
    po.categoryTbody.innerHTML = allCategories.length ? allCategories.map(x => `<tr>
      <td><strong>${escapeHtml(x.code)}</strong></td>
      <td>${escapeHtml(x.name)}</td>
      <td style="color:var(--sub);">${escapeHtml(GROUP_LABEL[x.group_name] || x.group_name || '—')}</td>
      <td>${activePill(x.is_active)}</td>
      <td><button class="btn ghost" data-category-edit="${escapeHtml(x.id)}">Edit</button></td>
    </tr>`).join('') : `<tr><td colspan="5" style="color:var(--sub);">No categories yet — create one so items can be filed under it.</td></tr>`;
  } catch (err) {
    po.categoryTbody.innerHTML = `<tr><td colspan="5" style="color:var(--err);">${escapeHtml(err.message || 'Failed to load')}</td></tr>`;
  }
}

// The category dialog is shared: Purchasing's category table and the New-item
// form's "+ New" shortcut both use it, so resolve + wire it independently of any
// one view.
let catDom = {};
let _catDlgWired = false;

function ensureCategoryDialog() {
  if (_catDlgWired) return;
  _catDlgWired = true;
  const g = (id) => document.getElementById(id);
  catDom = {
    dlg: g('invCategoryDlg'), form: g('invCategoryForm'), title: g('invCategoryDlgTitle'), submit: g('invCategorySubmit'),
    code: g('invCategoryCode'), name: g('invCategoryName'), group: g('invCategoryGroup'),
    description: g('invCategoryDescription'), activeField: g('invCategoryActiveField'), active: g('invCategoryActive'),
  };
  catDom.form && catDom.form.addEventListener('submit', onSaveCategory);
  wireAutoCode(catDom.name, catDom.code, { upper: false, maxLen: 64 });
}

function openCategory(existing) {
  ensureCategoryDialog();
  catDom.form.reset();
  resetAutoCode(catDom.code);
  editingCategoryId = existing ? existing.id : null;
  catDom.title.textContent = existing ? 'Edit category' : 'New category';
  catDom.submit.textContent = existing ? 'Save changes' : 'Create category';
  catDom.code.readOnly = !!existing;
  catDom.activeField.hidden = !existing;
  if (existing) {
    catDom.code.value = existing.code || '';
    catDom.name.value = existing.name || '';
    catDom.group.value = existing.group_name || 'network_customer';
    catDom.description.value = existing.description || '';
    catDom.active.checked = existing.is_active !== false;
  }
  openDialog(catDom.dlg);
}

async function onSaveCategory(ev) {
  ev.preventDefault();
  try {
    let created = null;
    if (editingCategoryId) {
      await api.fetch(API.inventoryCategory(editingCategoryId), {
        method: 'PATCH',
        body: JSON.stringify({
          name: catDom.name.value.trim(), group_name: catDom.group.value,
          description: catDom.description.value.trim() || null, is_active: catDom.active.checked,
        }),
      });
      flash('Category updated', 'success');
    } else {
      created = await api.fetch(API.inventoryCategories(), {
        method: 'POST',
        body: JSON.stringify({
          code: autoCodeValue(catDom.code, catDom.name, { maxLen: 64 }), name: catDom.name.value.trim(),
          group_name: catDom.group.value, description: catDom.description.value.trim() || null,
        }),
      });
      flash('Category created', 'success');
    }
    closeDialog(catDom.dlg);
    categories = [];          // bust the active-only cache
    await ensureLookups();    // reload it now so open dropdowns can refresh
    if (po && po.categoryTbody) loadCategoriesTable();
    // If the New-item form is open, refresh its dropdown and select the new one.
    if (el.itemDlg && el.itemDlg.open) {
      fillCategorySelect(el.itemCategory);
      if (created) el.itemCategory.value = created.id;
    }
  } catch (err) {
    flash(err.message || 'Failed to save category', 'error');
  }
}

// --- bulk import (download template → preview → commit) ---

let _importWired = false;
let imp = {};

function ensureImportDialog() {
  if (_importWired) return;
  _importWired = true;
  const g = (id) => document.getElementById(id);
  imp = { dlg: g('invImportDlg'), file: g('invImportFile'), result: g('invImportResult'), previewBtn: g('invImportPreviewBtn'), commitBtn: g('invImportCommitBtn') };
  imp.previewBtn && imp.previewBtn.addEventListener('click', () => runImport(false));
  imp.commitBtn && imp.commitBtn.addEventListener('click', () => runImport(true));
  imp.file && imp.file.addEventListener('change', () => { imp.commitBtn.disabled = true; imp.result.innerHTML = ''; });
}

function openImport() {
  ensureImportDialog();
  imp.file.value = '';
  imp.result.innerHTML = '';
  imp.commitBtn.disabled = true;
  openDialog(imp.dlg);
}

async function runImport(commitIt) {
  const file = imp.file.files && imp.file.files[0];
  if (!file) { flash('Choose a workbook first.', 'error'); return; }
  const fd = new FormData();
  fd.append('file', file);
  const btn = commitIt ? imp.commitBtn : imp.previewBtn;
  btn.disabled = true;
  try {
    const res = await api.fetch(commitIt ? API.inventoryImportCommit() : API.inventoryImportPreview(), { method: 'POST', body: fd });
    renderImportResult(res, commitIt);
    if (!commitIt) {
      imp.commitBtn.disabled = false;  // preview done — allow applying (best-effort per row)
    } else {
      flash('Import applied.', 'success');
      imp.commitBtn.disabled = true;
      categories = []; locations = [];  // bust caches so new categories/locations show
      if (po && po.categoryTbody) { loadCategoriesTable(); loadLocations(); }
    }
  } catch (err) {
    flash(err.message || 'Import failed', 'error');
  } finally {
    if (!(commitIt && imp.commitBtn.disabled)) btn.disabled = false;
  }
}

function renderImportResult(res, committed) {
  const rows = (res.sheets || []).map(s => {
    const errs = (s.errors || []).map(e => `<div style="color:var(--err); font-size:12px;">Row ${e.row}: ${escapeHtml(e.message)}</div>`).join('');
    return `<tr>
      <td><strong>${escapeHtml(s.sheet)}</strong></td>
      <td style="text-align:right;">${s.created}</td>
      <td style="text-align:right; color:var(--sub);">${s.skipped}</td>
      <td style="text-align:right;${s.errors.length ? ' color:var(--err);' : ''}">${s.errors.length}</td>
      <td>${errs || ''}</td>
    </tr>`;
  }).join('');
  imp.result.innerHTML = `
    <div style="margin-bottom:6px; font-weight:600;">${committed ? '✅ Imported' : 'Preview — nothing saved yet'} · ${res.total_errors} error${res.total_errors === 1 ? '' : 's'}</div>
    <div class="table-wrap"><table class="inv-mini-table">
      <thead><tr><th>Sheet</th><th style="text-align:right;">Created</th><th style="text-align:right;">Skipped</th><th style="text-align:right;">Errors</th><th>Details</th></tr></thead>
      <tbody>${rows}</tbody></table></div>`;
}

async function ensureSuppliers() {
  const d = await api.fetch(API.inventorySuppliers(), { method: 'GET' });
  suppliers = d.items || [];
}

async function loadSuppliers() {
  if (!po.supplierTbody) return;
  try {
    await ensureSuppliers();
    po.supplierTbody.innerHTML = suppliers.length ? suppliers.map(x => `<tr>
      <td><strong>${escapeHtml(x.code)}</strong></td>
      <td>${escapeHtml(x.name)}</td>
      <td style="color:var(--sub);">${escapeHtml(x.contact_name || x.email || x.phone || '—')}</td>
      <td>${x.is_active === false ? '<span style="color:var(--sub);">Inactive</span>' : 'Active'}</td>
      <td><button class="btn ghost" data-supplier-edit="${escapeHtml(x.id)}">Edit</button></td>
    </tr>`).join('') : `<tr><td colspan="5" style="color:var(--sub);">No suppliers yet.</td></tr>`;
  } catch (err) {
    po.supplierTbody.innerHTML = `<tr><td colspan="5" style="color:var(--err);">${escapeHtml(err.message || 'Failed to load')}</td></tr>`;
  }
}

async function loadLocations() {
  if (!po.locationTbody) return;
  try {
    const d = await api.fetch(API.inventoryLocations(), { method: 'GET' });
    allLocations = d.items || [];
    po.locationTbody.innerHTML = allLocations.length ? allLocations.map(x => `<tr>
      <td><strong>${escapeHtml(x.code)}</strong></td>
      <td>${escapeHtml(x.name)}</td>
      <td style="color:var(--sub);">${escapeHtml(x.kind)}</td>
      <td>${x.is_active === false ? '<span style="color:var(--sub);">Inactive</span>' : 'Active'}</td>
      <td><button class="btn ghost" data-location-edit="${escapeHtml(x.id)}">Edit</button></td>
    </tr>`).join('') : `<tr><td colspan="5" style="color:var(--sub);">No locations yet.</td></tr>`;
  } catch (err) {
    po.locationTbody.innerHTML = `<tr><td colspan="5" style="color:var(--err);">${escapeHtml(err.message || 'Failed to load')}</td></tr>`;
  }
}

async function loadPos() {
  if (!po.tbody) return;
  po.tbody.innerHTML = `<tr><td colspan="5" style="color:var(--sub);">Loading…</td></tr>`;
  try {
    const data = await api.fetch(API.inventoryPurchaseOrders(), { method: 'GET' });
    const items = data.items || [];
    _pos = items;
    po.tbody.innerHTML = items.length ? items.map(p => `<tr>
      <td><strong>${escapeHtml(p.po_number)}</strong></td>
      <td>${poStatusBadge(p.status)}</td>
      <td>${escapeHtml(p.expected_date || '')}</td>
      <td style="text-align:center;">${(p.lines || []).length}</td>
      <td><button class="btn" data-po-open="${escapeHtml(p.id)}">Open</button></td>
    </tr>`).join('') : `<tr><td colspan="5" style="color:var(--sub);">No purchase orders.</td></tr>`;
  } catch (err) {
    po.tbody.innerHTML = `<tr><td colspan="5" style="color:var(--err);">${escapeHtml(err.message || 'Failed to load')}</td></tr>`;
  }
}

async function openPoDetail(id) {
  try {
    const p = await api.fetch(API.inventoryPurchaseOrders() + '/' + encodeURIComponent(id), { method: 'GET' });
    activePo = p;
    po.detailEmpty.hidden = true;
    po.detailBody.hidden = false;
    po.detailName.textContent = p.po_number;
    po.detailMeta.innerHTML = poStatusBadge(p.status);
    const open = ['ordered', 'in_transit', 'partially_received'].includes(p.status);
    po.detailLines.innerHTML = (p.lines || []).map(l => {
      const outstanding = Number(l.qty_ordered) - Number(l.qty_received);
      const itemLabel = l.item_name ? `${l.item_sku ? l.item_sku + ' · ' : ''}${l.item_name}` : (l.item_id || '').slice(0, 8);
      return `<div style="padding:6px 0; border-bottom:1px solid var(--border-soft);">
        <strong>${escapeHtml(itemLabel)}</strong> · ordered ${fmt(l.qty_ordered)} · received ${fmt(l.qty_received)}
        ${open && outstanding > 0 ? ` <input type="number" step="0.001" min="0.001" placeholder="qty" data-recv-qty="${escapeHtml(l.id)}" style="width:80px;"> <input type="text" placeholder="serial" data-recv-serial="${escapeHtml(l.id)}" style="width:110px;"> <button class="btn" data-recv-line="${escapeHtml(l.id)}">Receive</button>` : ''}
      </div>`;
    }).join('') || '<span style="color:var(--sub);">No lines.</span>';
    const b = [];
    if (p.status === 'draft') b.push(`<button class="btn primary" data-po-action="submit">Submit</button>`);
    if (p.status === 'ordered') b.push(`<button class="btn" data-po-action="in-transit">Mark in transit</button>`);
    if (!['received', 'cancelled'].includes(p.status)) b.push(`<button class="btn" data-po-action="cancel">Cancel</button>`);
    po.detailActions.innerHTML = b.join(' ');
  } catch (err) {
    flash(err.message || 'Failed to load PO', 'error');
  }
}

async function onPoAction(ev) {
  const btn = ev.target.closest('button[data-po-action]');
  if (!btn || !activePo) return;
  try {
    await api.fetch(API.inventoryPurchaseOrders() + '/' + encodeURIComponent(activePo.id) + '/' + btn.dataset.poAction, { method: 'POST' });
    flash('Done', 'success');
    loadPos();
    openPoDetail(activePo.id);
  } catch (err) {
    flash(err.message || 'Action failed', 'error');
  }
}

async function onPoLineReceive(ev) {
  const btn = ev.target.closest('button[data-recv-line]');
  if (!btn || !activePo) return;
  const lineId = btn.dataset.recvLine;
  const qtyEl = po.detailLines.querySelector(`input[data-recv-qty="${lineId}"]`);
  const serialEl = po.detailLines.querySelector(`input[data-recv-serial="${lineId}"]`);
  const body = { line_id: lineId };
  if (qtyEl && qtyEl.value) body.quantity = qtyEl.value;
  if (serialEl && serialEl.value.trim()) body.serial = serialEl.value.trim();
  try {
    await api.fetch(API.inventoryPurchaseOrders() + '/' + encodeURIComponent(activePo.id) + '/receive', { method: 'POST', body: JSON.stringify(body) });
    flash('Received', 'success');
    openPoDetail(activePo.id);
    loadPos();
  } catch (err) {
    flash(err.message || 'Receive failed', 'error');
  }
}

// --- supplier + location + PO dialogs ---

function openSupplier(existing) {
  po.supplierForm.reset();
  resetAutoCode(po.supplierCode);
  editingSupplierId = existing ? existing.id : null;
  po.supplierDlgTitle.textContent = existing ? 'Edit supplier' : 'New supplier';
  po.supplierSubmit.textContent = existing ? 'Save changes' : 'Create supplier';
  po.supplierCode.readOnly = !!existing;
  po.supplierActiveField.hidden = !existing;
  if (existing) {
    po.supplierCode.value = existing.code || '';
    po.supplierName.value = existing.name || '';
    po.supplierContact.value = existing.contact_name || '';
    po.supplierEmail.value = existing.email || '';
    po.supplierPhone.value = existing.phone || '';
    po.supplierActive.checked = existing.is_active !== false;
  }
  openDialog(po.supplierDlg);
}

async function onSaveSupplier(ev) {
  ev.preventDefault();
  try {
    if (editingSupplierId) {
      const body = {
        name: po.supplierName.value.trim(),
        contact_name: po.supplierContact.value.trim() || null, email: po.supplierEmail.value.trim() || null,
        phone: po.supplierPhone.value.trim() || null, is_active: po.supplierActive.checked,
      };
      await api.fetch(API.inventorySupplier(editingSupplierId), { method: 'PATCH', body: JSON.stringify(body) });
      flash('Supplier updated', 'success');
    } else {
      const body = {
        code: autoCodeValue(po.supplierCode, po.supplierName, { upper: true, maxLen: 32 }), name: po.supplierName.value.trim(),
        contact_name: po.supplierContact.value.trim() || null, email: po.supplierEmail.value.trim() || null,
        phone: po.supplierPhone.value.trim() || null,
      };
      await api.fetch(API.inventorySuppliers(), { method: 'POST', body: JSON.stringify(body) });
      flash('Supplier created', 'success');
    }
    closeDialog(po.supplierDlg);
    loadSuppliers();
  } catch (err) {
    flash(err.message || 'Failed to save supplier', 'error');
  }
}

function openLocation(existing) {
  po.locationForm.reset();
  resetAutoCode(po.locationCode);
  editingLocationId = existing ? existing.id : null;
  po.locationDlgTitle.textContent = existing ? 'Edit location' : 'New location';
  po.locationSubmit.textContent = existing ? 'Save changes' : 'Create location';
  po.locationCode.readOnly = !!existing;
  po.locationKind.disabled = !!existing;
  po.locationActiveField.hidden = !existing;
  if (existing) {
    po.locationCode.value = existing.code || '';
    po.locationName.value = existing.name || '';
    po.locationKind.value = existing.kind || 'warehouse';
    po.locationAddress.value = existing.address || '';
    po.locationActive.checked = existing.is_active !== false;
  }
  openDialog(po.locationDlg);
}

async function onSaveLocation(ev) {
  ev.preventDefault();
  try {
    if (editingLocationId) {
      const body = {
        name: po.locationName.value.trim(),
        address: po.locationAddress.value.trim() || null, is_active: po.locationActive.checked,
      };
      await api.fetch(API.inventoryLocation(editingLocationId), { method: 'PATCH', body: JSON.stringify(body) });
      flash('Location updated', 'success');
    } else {
      const body = {
        code: autoCodeValue(po.locationCode, po.locationName, { upper: true, maxLen: 32 }), name: po.locationName.value.trim(),
        kind: po.locationKind.value, address: po.locationAddress.value.trim() || null,
      };
      await api.fetch(API.inventoryLocations(), { method: 'POST', body: JSON.stringify(body) });
      flash('Location created', 'success');
    }
    closeDialog(po.locationDlg);
    locations = [];  // bust the active-only dropdown cache so new/edited locations show
    loadLocations();
  } catch (err) {
    flash(err.message || 'Failed to save location', 'error');
  }
}

function openNewPo() {
  Promise.all([ensureSuppliers(), ensureLookups(), ensureItemsCache()]).then(() => {
    po.poForm.reset();
    po.poNumber.value = suggestPoNumber();
    fillSelect(po.poSupplier, suppliers, { labelFn: sName, placeholder: 'Select…' });
    fillSelect(po.poLocation, locations.filter(l => l.kind !== 'quarantine'), { labelFn: l => l.name, placeholder: 'Select…' });
    po.poLines.innerHTML = '';
    addPoLineRow();
    openDialog(po.poDlg);
  }).catch(() => flash('Failed to load suppliers', 'error'));
}

function sName(s) { return `${s.name} (${s.code})`; }

function addPoLineRow() {
  const opts = (_itemsCache || []).map(i => `<option value="${escapeHtml(i.id)}">${escapeHtml(i.sku)} — ${escapeHtml(i.name)}</option>`).join('');
  const row = document.createElement('div');
  row.className = 'inv-po-line-row';
  row.style.cssText = 'display:flex; gap:6px; margin-bottom:4px;';
  row.innerHTML = `<select class="inv-po-line-item" style="flex:1;"><option value="">Item…</option>${opts}</select>
    <input class="inv-po-line-qty" type="number" step="0.001" min="0.001" placeholder="qty" style="width:80px;">
    <input class="inv-po-line-cost" type="number" step="0.01" min="0" placeholder="cost" style="width:80px;">
    <button type="button" class="btn ghost inv-po-line-del">✕</button>`;
  row.querySelector('.inv-po-line-del').addEventListener('click', () => row.remove());
  po.poLines.appendChild(row);
}

async function onCreatePo(ev) {
  ev.preventDefault();
  const lines = [];
  po.poLines.querySelectorAll('.inv-po-line-row').forEach(row => {
    const item = row.querySelector('.inv-po-line-item').value;
    const qty = row.querySelector('.inv-po-line-qty').value;
    const cost = row.querySelector('.inv-po-line-cost').value;
    if (item && qty) lines.push({ item_id: item, qty_ordered: qty, unit_cost: cost || null });
  });
  if (!lines.length) { flash('Add at least one line', 'error'); return; }
  const body = {
    po_number: po.poNumber.value.trim(), supplier_id: po.poSupplier.value,
    expected_date: po.poExpected.value || null, fulfil_location_id: po.poLocation.value || null, lines,
  };
  try {
    await api.fetch(API.inventoryPurchaseOrders(), { method: 'POST', body: JSON.stringify(body) });
    closeDialog(po.poDlg);
    flash('Purchase order created', 'success');
    loadPos();
  } catch (err) {
    flash(err.message || 'Failed to create PO', 'error');
  }
}

// ---------------------------------------------------------------------------
// Utils
// ---------------------------------------------------------------------------

function fmt(v) {
  if (v == null) return '0';
  const n = Number(v);
  if (Number.isNaN(n)) return escapeHtml(String(v));
  return (n % 1 === 0) ? String(n) : String(n).replace(/0+$/, '').replace(/\.$/, '');
}

function debounce(fn, ms) {
  let t;
  return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); };
}

export default { init, initRequests, initLedger, initAssets, initPurchasing };
